import {
  doc,
  getDoc,
  getDocFromServer,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  getDocsFromServer,
  onSnapshot,
  writeBatch,
  waitForPendingWrites,
  Timestamp,
  serverTimestamp,
  FieldValue,
  orderBy,
  startAfter,
  limit as firestoreLimit,
  QueryDocumentSnapshot,
  type Query,
  type DocumentData,
  type QuerySnapshot,
} from 'firebase/firestore';
import { Platform } from 'react-native';
import { auth, db } from '@/lib/firebase';
import { getLandmarkImage, fetchAndCacheLandmarkImage, getDestinationSlug, clearCachedLandmarkImage, type LandmarkImageResult } from '@/utils/landmarkImages';
import type { BudgetAlertState, BudgetCategoryAllocations } from '@/types/budget';
import type { TravelMode } from '@/services/navigation';
import type {
  MapReferencePoint,
  FirestoreReferencePoint,
  DistanceDisplayMode,
  WalkingRadiusConfig,
} from '@/types/mapReferences';
import { networkMonitor } from './networkMonitor';
import {
  cacheTripData,
  getCachedTrips,
  queueTripUpdate,
  autoCacheActiveTrips,
  syncCacheWithTripIds,
  removeCachedTrip,
  clearAllCachedTrips,
} from './offlineTrips';
import { canUserAccessTripFromStoredData, filterTripsByUserAccess } from '@/utils/tripAccess';
import { coerceDate, fromLocalDateKey, toLocalDateKey } from '@/utils/dateKeys';
import { syncQueue } from './syncQueue';
import { realignTemplateItineraryToTripStart, shiftTripTemplateItineraryItemDates } from './itinerary';
import { logAnalyticsEvent } from './analyticsLogger';
import { functions, httpsCallable, httpsCallableWithTimeout } from '@/lib/functions';
import { isInternalDebugBuild } from './logger';
import { addBreadcrumb } from './errorTracking';
import { cancelNotificationsForTrip } from './notifications';
import { clearNotificationsForTrip } from '@/notifications/service';

/** High-volume Firestore trip subscription logs: dev only, or EXPO_PUBLIC_UI_DEBUG_LOGS=true */
function logTripsSubscriptionVerbose(...args: unknown[]): void {
  if (!isInternalDebugBuild()) return;
  // eslint-disable-next-line no-console
  console.log(...args);
}

type MergeTripsForOwnerRequest = { targetTripId: string; sourceTripIds: string[] };
type MergeTripsForOwnerResponse = { ok: true; auditId: string };
type SplitTripForOwnerRequest = {
  sourceTripId: string;
  newTripName: string;
  bookingIds: string[];
  itineraryItemIds?: string[];
};
type SplitTripForOwnerResponse = { ok: true; newTripId: string; bookingIdMap: Record<string, string> };
type UndoLastTripMergeResponse = { ok: true; undoneAuditId: string };

type LastKnownTripsEntry = {
  trips: Trip[];
  updatedAt: number;
};

const lastKnownTripsByUser = new Map<string, LastKnownTripsEntry>();
const memberIdsQueryDeniedByUser = new Set<string>();
/** Set when member/memberIds list queries returned permission-denied this session. */
const membershipListUnreliableByUser = new Set<string>();

/** `server` = write reached Firestore; `queued` = update was queued (offline, flaky network, or transient write failure). */
export type TripUpdateResult = 'server' | 'queued';

const pendingTripDeleters = new Map<string, Set<(tripId: string) => void>>();

function registerLiveTripDeleter(uid: string, onDelete: (tripId: string) => void): () => void {
  let set = pendingTripDeleters.get(uid);
  if (!set) {
    set = new Set();
    pendingTripDeleters.set(uid, set);
  }
  set.add(onDelete);
  return () => {
    set?.delete(onDelete);
    if (set && set.size === 0) {
      pendingTripDeleters.delete(uid);
    }
  };
}

const isReactNativeRuntime = Platform.OS !== 'web';
const enableRealtimeTripSubscriptions =
  (process.env.EXPO_PUBLIC_ENABLE_NATIVE_TRIPS_REALTIME ?? '').toLowerCase() === 'true' ||
  !isReactNativeRuntime;
/** Default on for all platforms (parity with web). Opt out with EXPO_PUBLIC_DISABLE_MEMBER_IDS_TRIP_QUERY=true. */
const enableMemberIdsTripQuery =
  (process.env.EXPO_PUBLIC_DISABLE_MEMBER_IDS_TRIP_QUERY ?? '').toLowerCase() !== 'true';
const NATIVE_TRIPS_POLL_MS = 15000;

export const getLastKnownTrips = (uid?: string | null): LastKnownTripsEntry | null => {
  if (!uid) return null;
  return lastKnownTripsByUser.get(uid) ?? null;
};

const updateLastKnownTrips = (uid: string, trips: Trip[]) => {
  lastKnownTripsByUser.set(uid, {
    trips,
    updatedAt: Date.now(),
  });
};

/** Per-user handlers that receive a full trip list (poll + realtime subscribe instances). */
const tripListReplayHandlersByUser = new Map<string, Set<(trips: Trip[]) => void>>();
const recoveredTripIdsByUser = new Map<string, Set<string>>();

function registerTripListReplayHandler(uid: string, handler: (trips: Trip[]) => void): () => void {
  let set = tripListReplayHandlersByUser.get(uid);
  if (!set) {
    set = new Set();
    tripListReplayHandlersByUser.set(uid, set);
  }
  set.add(handler);
  return () => {
    set!.delete(handler);
    if (set!.size === 0) {
      tripListReplayHandlersByUser.delete(uid);
    }
  };
}

function pushTripListToLiveSubscribers(uid: string, trips: Trip[]): void {
  const set = tripListReplayHandlersByUser.get(uid);
  if (!set) return;
  for (const fn of set) {
    try {
      fn(trips);
    } catch (e) {
      console.warn('[Trips] trip list replay handler failed', e);
    }
  }
}

function canUserAccessTripFromCachedData(uid: string, trip: Trip): boolean {
  return canUserAccessTripFromStoredData(uid, trip);
}

function mergeWithRecoverableLastKnownTrips(uid: string, trips: Trip[]): Trip[] {
  const recoveredIds = recoveredTripIdsByUser.get(uid);
  const lastKnown = getLastKnownTrips(uid)?.trips ?? [];
  if (!recoveredIds?.size && !lastKnown.length) {
    return trips;
  }

  const byId = new Map(trips.map((trip) => [trip.id, trip]));
  let addedRecoveredTrip = false;
  for (const trip of lastKnown) {
    // Only explicitly recovered trip IDs (e.g. right after accept invite). Do not re-merge all
    // last-known shared trips when membership queries are flaky — stale cache can still list
    // collaborators who left or trips the user no longer belongs to.
    const shouldRecover = recoveredIds?.has(trip.id) ?? false;
    if (shouldRecover && !byId.has(trip.id) && canUserAccessTripFromCachedData(uid, trip)) {
      byId.set(trip.id, trip);
      addedRecoveredTrip = true;
    }
  }

  return addedRecoveredTrip ? sortTripsByStartDateStable(Array.from(byId.values())) : trips;
}

/**
 * After accepting invites or when membership list queries are flaky, keep trip IDs in the
 * recovery set so refresh/replay does not drop them before Firestore catches up.
 */
export function markTripsForListRecovery(uid: string, tripIds: string[]): void {
  if (!tripIds.length) {
    return;
  }
  const set = recoveredTripIdsByUser.get(uid) ?? new Set<string>();
  for (const tripId of tripIds) {
    if (tripId) {
      set.add(tripId);
    }
  }
  recoveredTripIdsByUser.set(uid, set);
}

/**
 * Refetch the user's trips from Firestore, update session cache, and sync all `subscribeToUserTrips` listeners.
 * Use after archive / merge / split so the UI does not wait for the next poll (~15s on native).
 */
export async function refreshTripListFromServer(uid: string): Promise<Trip[]> {
  const { trips } = await fetchUserTrips(uid, 100);
  const mergedTrips = mergeWithRecoverableLastKnownTrips(uid, trips);
  updateLastKnownTrips(uid, mergedTrips);
  pushTripListToLiveSubscribers(uid, mergedTrips);
  return mergedTrips;
}

/**
 * After a successful trip write, push the merged trip into all `subscribeToUserTrips` listeners
 * so native (15s poll) and any lagging streams update immediately.
 */
export function notifyLiveTripUpdated(uid: string, trip: Trip): void {
  const reconciledTrip = reconcileTripRecord(trip);
  const recoveredIds = recoveredTripIdsByUser.get(uid) ?? new Set<string>();
  recoveredIds.add(reconciledTrip.id);
  recoveredTripIdsByUser.set(uid, recoveredIds);

  const prev = getLastKnownTrips(uid)?.trips ?? [];
  if (!prev.length) {
    void refreshTripListFromServer(uid).catch(() => {});
    return;
  }
  const idx = prev.findIndex((t) => t.id === reconciledTrip.id);
  const next =
    idx >= 0
      ? prev.map((t, i) => (i === idx ? mergeTwoTripRecords(t, reconciledTrip) : t))
      : [...prev, reconciledTrip];
  const sorted = sortTripsByStartDateStable(next);
  updateLastKnownTrips(uid, sorted);
  pushTripListToLiveSubscribers(uid, sorted);
}

/**
 * When a trip is deleted, or the current user leaves a shared trip, remove it from in-memory
 * subscription state immediately so the UI does not wait for the next onSnapshot (or 15s poll).
 * Also prunes the session's last-known list so in-memory data matches.
 */
/**
 * Clears in-memory trip list state and query guard flags (e.g. after leave trip or
 * "reset cache" from settings). Does not clear Firestore IndexedDB — use
 * {@link resetAllTripListLocalState} for a full local reset on web.
 */
export function resetTripListSessionState(uid: string): void {
  if (!uid) {
    return;
  }
  lastKnownTripsByUser.delete(uid);
  recoveredTripIdsByUser.delete(uid);
  membershipListUnreliableByUser.delete(uid);
  memberIdsQueryDeniedByUser.delete(uid);
  pushTripListToLiveSubscribers(uid, []);
}

/**
 * Full local reset: offline trip packs, session list, and (web) Firestore IndexedDB cache.
 * Call when Storage shows 0 trips but stale shared trips still flash in the UI.
 */
export async function resetAllTripListLocalState(uid: string): Promise<{
  firestorePersistenceCleared: boolean;
}> {
  resetTripListSessionState(uid);
  await clearAllCachedTrips();

  let firestorePersistenceCleared = false;
  if (Platform.OS === 'web') {
    try {
      const { clearIndexedDbPersistence } = await import('firebase/firestore');
      await clearIndexedDbPersistence(db);
      firestorePersistenceCleared = true;
    } catch (error) {
      console.warn(
        '[Trips] clearIndexedDbPersistence failed (active listeners?). Reload the app after reset.',
        error,
      );
    }
  }

  return { firestorePersistenceCleared };
}

export function notifyLiveTripDeleted(uid: string, tripId: string): void {
  recoveredTripIdsByUser.get(uid)?.delete(tripId);

  const last = getLastKnownTrips(uid);
  let pruned: Trip[] | null = null;
  if (last?.trips?.length) {
    pruned = last.trips.filter((t) => t.id !== tripId);
    if (pruned.length !== last.trips.length) {
      updateLastKnownTrips(uid, pruned);
    }
  }
  void removeCachedTrip(tripId).catch((err) => {
    console.warn('[Trips] Failed to remove offline cache for deleted/left trip', tripId, err);
  });
  const set = pendingTripDeleters.get(uid);
  if (set) {
    for (const fn of set) {
      try {
        fn(tripId);
      } catch (e) {
        console.warn('[Trips] live trip deleter failed', e);
      }
    }
  }
  if (pruned) {
    pushTripListToLiveSubscribers(uid, pruned);
  }
}

const isPermissionDeniedError = (error: unknown): boolean => {
  const code = (error as { code?: string } | null)?.code;
  return code === 'permission-denied' || code === 'missing-or-insufficient-permissions';
};

const isOptionalTripMembershipQueryDenied = (error: unknown): boolean => {
  if (!isPermissionDeniedError(error)) return false;
  const message = String((error as { message?: string } | null)?.message ?? '').toLowerCase();
  return (
    message.includes('missing or insufficient permissions') ||
    message.includes('permission-denied') ||
    message.includes('insufficient permissions')
  );
};

/** Prime Auth token before list reads — reduces transient `permission-denied` after cold start / re-login on native. */
async function primeAuthTokenBeforeFirestoreReads(expectedUid: string): Promise<void> {
  const user = auth?.currentUser ?? null;
  if (!user || user.uid !== expectedUid) {
    return;
  }
  try {
    await user.getIdToken();
  } catch {
    // Non-fatal: Firestore may still attach recovered credentials on the transport.
  }
}

type ListAccessibleTripsResponse = {
  trips: Array<Record<string, unknown> & { id: string }>;
};

let listAccessibleTripsInFlight: Promise<Trip[]> | null = null;

/**
 * Server-side trip list when client Firestore membership queries return permission-denied.
 */
async function fetchAccessibleTripsViaCallable(uid: string, limitCount: number): Promise<Trip[]> {
  if (listAccessibleTripsInFlight) {
    return listAccessibleTripsInFlight;
  }
  listAccessibleTripsInFlight = (async () => {
    try {
      console.warn(
        '[Trips] Membership Firestore queries failed; loading trips via listAccessibleTrips callable.',
        { uid, limitCount },
      );
      const callable = httpsCallable<{ limit?: number }, ListAccessibleTripsResponse>(
        functions,
        'listAccessibleTrips',
      );
      const { data } = await callable({ limit: limitCount });
      const raw = Array.isArray(data?.trips) ? data.trips : [];
      const trips: Trip[] = [];
      for (const item of raw) {
        const id = typeof item?.id === 'string' ? item.id : '';
        if (!id) {
          continue;
        }
        const { id: _tripId, ...rest } = item;
        try {
          trips.push(convertFirestoreTrip(id, rest as FirestoreTrip));
        } catch (error) {
          console.warn('[Trips] Skipping trip from listAccessibleTrips', id, error);
        }
      }
      console.warn('[Trips] listAccessibleTrips callable completed', {
        uid,
        rawCount: raw.length,
        tripCount: trips.length,
      });
      return trips;
    } catch (error) {
      console.error('[Trips] listAccessibleTrips callable failed', error);
      return [];
    } finally {
      listAccessibleTripsInFlight = null;
    }
  })();
  return listAccessibleTripsInFlight;
}

/** On web when online, avoid pre-hydrating shared trips from session/disk before the server list arrives. */
export function shouldHydrateTripFromSessionCache(uid: string, trip: Trip): boolean {
  if (!trip?.id || !canUserAccessTripFromStoredData(uid, trip)) {
    return false;
  }
  if (Platform.OS === 'web' && networkMonitor.isOnline() && trip.owner !== uid) {
    return false;
  }
  return true;
}

function shouldSkipStaleSharedTripSnapshot(
  trip: Trip,
  fromCache: boolean,
  serverAuthoritativeTripIds: Set<string>,
): boolean {
  if (!fromCache || !trip.id || trip.owner === uid || serverAuthoritativeTripIds.size === 0) {
    return false;
  }
  return !serverAuthoritativeTripIds.has(trip.id);
}

/** Replace live subscription map with a server-backed trip list (drops left/revoked shared trips). */
export function applyAuthoritativeTripListToLiveMap(
  uid: string,
  allTrips: Map<string, Trip>,
  ownerTripIds: Set<string>,
  memberTripIds: Set<string>,
  memberIdsTripIds: Set<string>,
  authoritativeTrips: Trip[],
  serverAuthoritativeTripIds: Set<string>,
): void {
  const authoritativeIds = new Set(
    authoritativeTrips.map((t) => t.id).filter((id): id is string => Boolean(id)),
  );
  serverAuthoritativeTripIds.clear();
  authoritativeIds.forEach((id) => serverAuthoritativeTripIds.add(id));

  for (const tripId of [...allTrips.keys()]) {
    if (!authoritativeIds.has(tripId)) {
      allTrips.delete(tripId);
      ownerTripIds.delete(tripId);
      memberTripIds.delete(tripId);
      memberIdsTripIds.delete(tripId);
    }
  }

  for (const t of authoritativeTrips) {
    if (!t?.id) {
      continue;
    }
    if (!canUserAccessTripFromCachedData(uid, t)) {
      allTrips.delete(t.id);
      ownerTripIds.delete(t.id);
      memberTripIds.delete(t.id);
      memberIdsTripIds.delete(t.id);
      continue;
    }
    setTripInMapIfNewer(allTrips, t);
    if (t.owner === uid) {
      ownerTripIds.add(t.id);
      memberTripIds.delete(t.id);
      memberIdsTripIds.delete(t.id);
    } else {
      memberTripIds.add(t.id);
      ownerTripIds.delete(t.id);
    }
  }
}

function mergeCallableTripsIntoLiveSubscription(
  uid: string,
  allTrips: Map<string, Trip>,
  ownerTripIds: Set<string>,
  memberTripIds: Set<string>,
  memberIdsTripIds: Set<string>,
  callableTrips: Trip[],
  serverAuthoritativeTripIds: Set<string>,
): void {
  applyAuthoritativeTripListToLiveMap(
    uid,
    allTrips,
    ownerTripIds,
    memberTripIds,
    memberIdsTripIds,
    callableTrips,
    serverAuthoritativeTripIds,
  );
}

/** Prefer server snapshots for trip list reads so collaborators don't keep stale dates from local cache. */
async function getDocsPreferServer(
  firestoreQuery: Query<DocumentData, DocumentData>,
): Promise<QuerySnapshot<DocumentData, DocumentData>> {
  try {
    return await getDocsFromServer(firestoreQuery);
  } catch (error) {
    return await getDocsWithCredentialRetry(firestoreQuery);
  }
}

/** One-shot token refresh retry when Firestore returns permission-denied (rules see no auth briefly). */
async function getDocsWithCredentialRetry(
  firestoreQuery: Query<DocumentData, DocumentData>,
): Promise<QuerySnapshot<DocumentData, DocumentData>> {
  try {
    return await getDocs(firestoreQuery);
  } catch (error) {
    const user = auth?.currentUser ?? null;
    if (!user || !isPermissionDeniedError(error)) {
      throw error;
    }
    try {
      await user.getIdToken(true);
      await new Promise<void>((resolve) => setTimeout(resolve, 280));
    } catch {
      // Retry getDocs anyway.
    }
    return await getDocs(firestoreQuery);
  }
}

/**
 * Test Firestore connectivity by attempting a simple read operation.
 * Useful for diagnosing cases where subscriptions never fire.
 * Returns true if connection works, false otherwise.
 */
export const testFirestoreConnectivity = async (uid?: string): Promise<{
  success: boolean;
  latencyMs: number;
  error?: string;
}> => {
  const startTime = Date.now();
  try {
    console.log('[testFirestoreConnectivity] 🔵 Testing Firestore connection (v3)...');

    // Security Rules require 'owner' == auth.uid.
    // Reading a random doc like 'connectivity_ping' fails because it has no owner.
    // We must use a query that satisfies the rules.
    if (uid) {
      console.log('[testFirestoreConnectivity] 🔵 Querying trips for authenticated owner');
      const q = query(
        collection(db, 'trips'),
        where('owner', '==', uid),
        firestoreLimit(1)
      );
      await getDocs(q);
    } else {
      // Fallback for unauthed (unlikely in this context, but safe)
      // This will likely fail rules if unauthed, but it's a connection test.
      const testRef = doc(db, 'trips', 'connectivity_ping');
      await getDoc(testRef);
    }

    const latencyMs = Date.now() - startTime;
    console.log(`[testFirestoreConnectivity] ✅ Firestore connected! Latency: ${latencyMs}ms`);
    return { success: true, latencyMs };
  } catch (error: any) {
    const latencyMs = Date.now() - startTime;
    console.error('[testFirestoreConnectivity] ❌ Firestore connection FAILED:', error);
    console.error('[testFirestoreConnectivity] ❌ Error code:', error?.code);
    console.error('[testFirestoreConnectivity] ❌ Error message:', error?.message);
    return {
      success: false,
      latencyMs,
      error: `${error?.code || ''} ${error?.message || String(error)}`.trim(),
    };
  }
};

export const DEFAULT_BUDGET_ALERT_THRESHOLDS: number[] = [0.8, 0.9, 1.0];

const coerceToValidDate = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed =
      typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
        ? fromLocalDateKey(value)
        : new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

type ResolveImageOptions = {
  forceRefresh?: boolean;
  identityKey?: string;
  countryName?: string | null;
};

const resolveLandmarkImageResult = async (
  destination?: string,
  options?: ResolveImageOptions,
): Promise<LandmarkImageResult | null> => {
  if (!destination) return null;
  try {
    return await fetchAndCacheLandmarkImage(destination, {
      forceRefresh: options?.forceRefresh,
      identityKey: options?.identityKey,
      countryName: options?.countryName,
    });
  } catch (error) {
    console.warn('[trips] Failed to resolve landmark image', error);
    const fallback = getLandmarkImage(destination);
    if (!fallback) {
      return null;
    }
    return {
      url: fallback,
      citySlug: getDestinationSlug(destination),
      source: 'static',
    };
  }
};

const applyLandmarkImageToTrip = (
  target: Partial<FirestoreTrip>,
  result: LandmarkImageResult | null,
): void => {
  if (!result?.url) {
    return;
  }
  target.landmarkImage = result.url;
  target.landmarkImageCredit = result.creditText ?? null;
  target.landmarkImageCreditUrl = result.creditUrl ?? null;
};

export type CollaboratorPermission = 'view' | 'edit' | 'add_items';

export type Collaborator = {
  userId: string;
  email: string;
  name?: string;
  permission: CollaboratorPermission;
  addedAt: Date;
  addedBy: string;
};

export type FormerTripMember = {
  userId: string;
  email?: string;
  name?: string;
  leftAt?: Date;
  leftBy?: string;
};

export type TripDestination = {
  city: string;
  country?: string;
  /**
   * ISO 3166-1 alpha-2 country code for the destination country (e.g., "JP").
   * Used for services that require stable country identifiers (visa, advisories, etc).
   */
  countryCode?: string;
  /**
   * Optional local time (HH:mm) for the day you arrive/start this destination.
   * Used for overlap/travel days where multiple destinations share a calendar date.
   */
  startTime?: string;
  /**
   * Optional local time (HH:mm) for the day you depart/end this destination.
   * Used for overlap/travel days where multiple destinations share a calendar date.
   */
  endTime?: string;
  /**
   * Stable identifier for a verified location.
   * (OpenWeather geocoding doesn't provide a placeId, so we generate one from coords + country.)
   */
  locationId?: string;
  /**
   * Optional canonical place identifier when available from upstream providers.
   * Preferred for image caching and destination-level identity.
   */
  placeId?: string;
  startDate: Date;
  endDate: Date;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
};

export type Trip = {
  id: string;
  name: string;
  startDate: Date;
  endDate: Date;
  status?: string;
  landmarkImage?: string;
  landmarkImageCredit?: string | null;
  landmarkImageCreditUrl?: string | null;
  destination?: string; // Legacy: single destination string (for backward compatibility)
  destinations?: TripDestination[]; // New: multi-city destinations with dates
  travelingFrom?: string;
  travelMode?: string; // Transportation to destination (plane, car, train, etc.)
  preferredTravelMode?: TravelMode; // Local travel mode for itinerary scheduling (walking, driving, transit)
  emailSyncEnabled?: boolean;
  owner?: string;
  /** Display name for trip owner (Firestore `ownerName`); used when collaborator list lacks owner entry. */
  ownerName?: string;
  members?: string[];
  /** Denormalized member ids (mirrors server indexing); used for free-tier limit parity with Cloud Functions. */
  memberIds?: string[];
  collaborators?: Collaborator[];
  formerMembers?: FormerTripMember[];
  lastEditedBy?: string;
  lastEditedAt?: Date;
  budget?: number | null;
  locale?: string;
  baseCurrency?: string; // Base currency for budget calculations (defaults to USD)
  /**
   * Optional per-category budget allocation fractions (0..1) that sum to 1.
   * When absent, UI uses defaults.
   */
  budgetCategoryAllocations?: BudgetCategoryAllocations;
  createdAt?: Date;
  updatedAt?: Date;
  isArchived?: boolean;
  budgetAlertThresholds?: number[];
  budgetAlertState?: BudgetAlertState;
  referencePoints?: MapReferencePoint[];
  primaryReferencePointId?: string;
  distanceDisplayMode?: DistanceDisplayMode;
  walkingRadiusConfig?: WalkingRadiusConfig;
  travelStyles?: string[];
  isPublic?: boolean;
  /** Optional primary map pin when the trip has a single resolved destination (legacy / UI convenience). */
  destinationCoordinates?: { latitude: number; longitude: number };
};

export type FirestoreCollaborator = {
  userId: string;
  email: string;
  name?: string;
  permission: CollaboratorPermission;
  addedAt: Timestamp | string;
  addedBy: string;
};

export type FirestoreTripDestination = {
  city: string;
  country?: string;
  countryCode?: string;
  startTime?: string;
  endTime?: string;
  locationId?: string;
  placeId?: string;
  startDate: Timestamp | string;
  endDate: Timestamp | string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
};

export type BudgetUpdateMode = 'auto-set' | 'auto-add' | 'manual';

export type FirestoreTrip = {
  owner: string;
  members: string[];
  /** Optional; when set, entitlement queries use this alongside `members`. */
  memberIds?: string[];
  status: string;
  title: string;
  destination?: string; // Legacy: single destination string
  destinations?: FirestoreTripDestination[]; // New: multi-city destinations
  travelingFrom?: string;
  startDate: Timestamp | string;
  endDate: Timestamp | string;
  travelMode?: string;
  preferredTravelMode?: string; // TravelMode as string for Firestore
  emailSyncEnabled?: boolean;
  landmarkImage?: string;
  landmarkImageCredit?: string | null;
  landmarkImageCreditUrl?: string | null;
  collaborators?: FirestoreCollaborator[];
  formerMembers?: Array<{
    userId: string;
    email?: string;
    name?: string;
    leftAt?: Timestamp | string;
    leftBy?: string;
  }>;
  ownerName?: string;
  lastEditedBy?: string;
  lastEditedAt?: Timestamp | string | FieldValue;
  createdAt: Timestamp | string | FieldValue;
  updatedAt: Timestamp | string | FieldValue;
  budgetUpdateMode?: BudgetUpdateMode; // How to handle budget updates from bookings
  budget: number | null;
  locale: string;
  baseCurrency?: string;
  budgetCategoryAllocations?: BudgetCategoryAllocations;
  isArchived?: boolean;
  budgetAlertThresholds?: number[];
  budgetAlertState?: BudgetAlertState;
  referencePoints?: FirestoreReferencePoint[];
  primaryReferencePointId?: string;
  distanceDisplayMode?: DistanceDisplayMode;
  walkingRadiusConfig?: WalkingRadiusConfig;
};

export type CreateTripData = {
  name: string;
  destination?: string; // Legacy: single destination string
  destinations?: TripDestination[]; // New: multi-city destinations with dates
  travelingFrom?: string;
  startDate: Date;
  endDate: Date;
  travelMode: string;
  preferredTravelMode?: TravelMode;
  emailSyncEnabled?: boolean;
  budgetUpdateMode?: BudgetUpdateMode; // How to handle budget updates from bookings
  referencePoints?: MapReferencePoint[];
  primaryReferencePointId?: string;
  distanceDisplayMode?: DistanceDisplayMode;
  walkingRadiusConfig?: WalkingRadiusConfig;
  /** Base currency for budget tracking - defaults to user's default currency or USD */
  baseCurrency?: string;
  /**
   * When true, skip blocking work (like fetching landmark images) during creation.
   * Useful for Smart Upload where we want a very fast "trip created" response.
   */
  skipLandmarkImage?: boolean;
};

const convertReferencePointsFromFirestore = (
  points?: FirestoreReferencePoint[],
): MapReferencePoint[] | undefined => {
  if (!points?.length) {
    return undefined;
  }

  return points.map((point) => {
    const createdAtRaw = point.createdAt;
    let createdAt: Date | undefined;

    if (createdAtRaw instanceof Timestamp) {
      createdAt = createdAtRaw.toDate();
    } else if (typeof createdAtRaw === 'string' || typeof createdAtRaw === 'number') {
      createdAt = new Date(createdAtRaw);
    } else if (createdAtRaw instanceof Date) {
      createdAt = createdAtRaw;
    }

    return {
      id: point.id,
      type: point.type,
      title: point.title,
      address: point.address,
      coordinates: point.coordinates,
      isPrimary: point.isPrimary,
      icon: point.icon,
      createdAt,
    };
  });
};

const serializeReferencePointsForWrite = (
  points?: MapReferencePoint[],
): FirestoreReferencePoint[] | undefined => {
  if (!points?.length) {
    return undefined;
  }

  return points.map((point) => ({
    id: point.id,
    type: point.type,
    title: point.title,
    address: point.address,
    coordinates: point.coordinates,
    isPrimary: point.isPrimary,
    icon: point.icon,
    createdAt: Timestamp.fromDate(point.createdAt ?? new Date()),
  })) as unknown as FirestoreReferencePoint[];
};

/** Store calendar dates at UTC midnight so reads round-trip through coerceTimestampDateOnlyToLocal. */
function timestampFromLocalDateOnly(date: Date): Timestamp {
  const key = toLocalDateKey(date);
  const [y, m, d] = key.split('-').map((v) => parseInt(v, 10));
  return Timestamp.fromMillis(Date.UTC(y, m - 1, d));
}

function normalizeCalendarDay(date: Date | null | undefined | unknown): Date | null {
  const resolved = date instanceof Date ? date : coerceDate(date);
  if (!resolved || !Number.isFinite(resolved.getTime())) {
    return null;
  }
  const out = new Date(resolved);
  out.setHours(0, 0, 0, 0);
  return out;
}

function calendarSpanDays(start: Date, end: Date): number {
  return Math.round((normalizeCalendarDay(end)!.getTime() - normalizeCalendarDay(start)!.getTime()) / 86400000);
}

function tripDateMs(value: Date | undefined | null): number {
  const normalized = normalizeCalendarDay(
    value instanceof Date ? value : coerceDate(value) ?? null,
  );
  return normalized ? normalized.getTime() : 0;
}

/** Align top-level trip dates with nested destinations so every screen shows the same range. */
export function reconcileTripDateFields(
  topStart: Date | null | undefined,
  topEnd: Date | null | undefined,
  destinations: TripDestination[] | undefined,
): { startDate: Date; endDate: Date; destinations: TripDestination[] | undefined } {
  let start = normalizeCalendarDay(topStart ?? null);
  let end = normalizeCalendarDay(topEnd ?? topStart ?? null);

  const dests = destinations?.length
    ? destinations.map((dest) => ({
        ...dest,
        startDate: normalizeCalendarDay(coerceDate(dest.startDate) ?? dest.startDate) ?? dest.startDate,
        endDate: normalizeCalendarDay(coerceDate(dest.endDate) ?? dest.endDate) ?? dest.endDate,
      }))
    : undefined;

  const destStarts: Date[] = [];
  const destEnds: Date[] = [];
  if (dests?.length) {
    for (const dest of dests) {
      const ds = normalizeCalendarDay(coerceDate(dest.startDate));
      const de = normalizeCalendarDay(coerceDate(dest.endDate));
      if (ds) destStarts.push(ds);
      if (de) destEnds.push(de);
    }
  }

  const destMin =
    destStarts.length > 0 ? new Date(Math.min(...destStarts.map((d) => d.getTime()))) : null;
  const destMax =
    destEnds.length > 0 ? new Date(Math.max(...destEnds.map((d) => d.getTime()))) : null;
  const topSpan = start && end ? calendarSpanDays(start, end) : -1;
  const destSpan = destMin && destMax ? calendarSpanDays(destMin, destMax) : -1;

  // Legacy/migrated docs: top-level is a 1-day placeholder while destinations hold the real window.
  if (destMin && destMax && destSpan >= 1 && topSpan < 1) {
    start = destMin;
    end = destMax;
  }

  let syncedDests = dests;
  if (start && end && syncedDests?.length === 1 && topSpan >= 1) {
    syncedDests = [{ ...syncedDests[0], startDate: start, endDate: end }];
  }

  const fallback = fromLocalDateKey('1970-01-01');
  return {
    startDate: start ?? destMin ?? fallback,
    endDate: end ?? destMax ?? start ?? destMin ?? fallback,
    destinations: syncedDests,
  };
}

function reconcileTripRecord(trip: Trip): Trip {
  const reconciled = reconcileTripDateFields(trip.startDate, trip.endDate, trip.destinations);
  return {
    ...trip,
    startDate: reconciled.startDate,
    endDate: reconciled.endDate,
    destinations: reconciled.destinations,
  };
}

/** Normalize trip dates after disk cache hydration or optimistic UI merges. */
export function normalizeTripForClient(trip: Trip): Trip {
  return reconcileTripRecord(trip);
}

function lastEditedAtMs(trip: Pick<Trip, 'lastEditedAt'>): number {
  const lastEdited = trip.lastEditedAt;
  if (lastEdited instanceof Date) {
    return lastEdited.getTime();
  }
  if (typeof lastEdited === 'string' || typeof lastEdited === 'number') {
    const parsed = new Date(lastEdited).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function tripDatesDiffer(a: Trip, b: Trip): boolean {
  return (
    tripDateMs(a.startDate) !== tripDateMs(b.startDate) ||
    tripDateMs(a.endDate) !== tripDateMs(b.endDate)
  );
}

/**
 * Merge two trip rows for list state. When dates disagree, prefer the row with the latest
 * `lastEditedAt` for date fields (true trip edits) instead of a newer `updatedAt` alone
 * (e.g. membership sync bumps that must not revert committed date changes).
 */
export function mergeTwoTripRecords(existing: Trip, incoming: Trip): Trip {
  const e = reconcileTripRecord(existing);
  const i = reconcileTripRecord(incoming);

  if (!tripDatesDiffer(e, i)) {
    return tripFreshnessMs(i) >= tripFreshnessMs(e) ? i : e;
  }

  const editE = lastEditedAtMs(e);
  const editI = lastEditedAtMs(i);
  if (editE !== editI) {
    const dateSource = editI > editE ? i : e;
    const metaSource = tripFreshnessMs(i) >= tripFreshnessMs(e) ? i : e;
    return reconcileTripRecord({
      ...metaSource,
      startDate: dateSource.startDate,
      endDate: dateSource.endDate,
      destinations: dateSource.destinations,
      lastEditedAt: dateSource.lastEditedAt ?? metaSource.lastEditedAt,
      lastEditedBy: dateSource.lastEditedBy ?? metaSource.lastEditedBy,
    });
  }

  return tripFreshnessMs(i) >= tripFreshnessMs(e) ? i : e;
}

const coerceTimestampDateOnlyToLocal = (ts: Timestamp): Date => {
  const d = ts.toDate();
  return coerceUtcMidnightTimestampToLocalDate(d);
};

function coerceUtcMidnightTimestampToLocalDate(d: Date): Date {
  // If the timestamp is exactly midnight UTC, it is very likely a "date-only" value
  // that was created from `new Date('YYYY-MM-DD')` (UTC interpretation). Convert it
  // back into a local calendar date to avoid timezone day-shifts.
  if (
    d.getUTCHours() === 0 &&
    d.getUTCMinutes() === 0 &&
    d.getUTCSeconds() === 0 &&
    d.getUTCMilliseconds() === 0
  ) {
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return fromLocalDateKey(`${y}-${m}-${day}`);
  }
  return d;
}

const coerceStringDateOnlyToLocal = (value: string): Date => {
  const dateOnlyMatch = /^(\d{4}-\d{2}-\d{2})$/.exec(value);
  const isoMidnightUtcMatch = /^(\d{4}-\d{2}-\d{2})T00:00:00(?:\.000)?Z$/.exec(value);
  if (dateOnlyMatch) return fromLocalDateKey(dateOnlyMatch[1]);
  if (isoMidnightUtcMatch) return fromLocalDateKey(isoMidnightUtcMatch[1]);
  return new Date(value);
};

const readTripCalendarDate = (value: unknown, fallback: Date = new Date()): Date =>
  parseTripFirestoreDateField(value) ?? fallback;

const tripStartCalendarKeysDiffer = (oldStart: Date, newStart: Date): boolean =>
  toLocalDateKey(oldStart) !== toLocalDateKey(newStart);

/** Parse trip date fields from Firestore SDK, JSON cache, or callable `{seconds,nanoseconds}` payloads. */
export function parseTripFirestoreDateField(value: unknown): Date | null {
  if (value == null) {
    return null;
  }
  if (value instanceof Timestamp) {
    return coerceTimestampDateOnlyToLocal(value);
  }
  if (typeof value === 'string') {
    return coerceStringDateOnlyToLocal(value);
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    const asDate = new Date(value);
    return coerceTimestampDateOnlyToLocal(Timestamp.fromDate(asDate));
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const seconds =
      typeof obj.seconds === 'number'
        ? obj.seconds
        : typeof obj._seconds === 'number'
          ? obj._seconds
          : null;
    if (seconds != null) {
      const nanos =
        typeof obj.nanoseconds === 'number'
          ? obj.nanoseconds
          : typeof obj._nanoseconds === 'number'
            ? obj._nanoseconds
            : 0;
      const ms = seconds * 1000 + Math.floor(nanos / 1e6);
      return coerceUtcMidnightTimestampToLocalDate(new Date(ms));
    }
    const coerced = coerceDate(value);
    if (coerced) {
      return coerceTimestampDateOnlyToLocal(Timestamp.fromDate(coerced));
    }
  }
  return null;
}

// Convert Firestore trip to app Trip type
const convertFirestoreTrip = (id: string, data: FirestoreTrip): Trip => {
  const rawStart = parseTripFirestoreDateField(data.startDate);
  const rawEnd = parseTripFirestoreDateField(data.endDate);

  const collaborators: Collaborator[] | undefined = data.collaborators?.map((collab) => ({
    userId: collab.userId,
    email: collab.email,
    name: collab.name,
    permission: collab.permission,
    addedAt: collab.addedAt instanceof Timestamp
      ? collab.addedAt.toDate()
      : typeof collab.addedAt === 'string'
        ? new Date(collab.addedAt)
        : new Date(),
    addedBy: collab.addedBy,
  }));
  const formerMembers: FormerTripMember[] | undefined = data.formerMembers?.map((member) => ({
    userId: member.userId,
    email: member.email,
    name: member.name,
    leftBy: member.leftBy,
    leftAt: member.leftAt instanceof Timestamp
      ? member.leftAt.toDate()
      : typeof member.leftAt === 'string'
        ? new Date(member.leftAt)
        : undefined,
  }));

  const coerceDestinationDate = (value: unknown, fallback: Date | null): Date | null => {
    return parseTripFirestoreDateField(value) ?? fallback;
  };

  // Convert destinations array if present (never default missing dates to "today").
  const destinations: TripDestination[] | undefined = data.destinations
    ?.map((dest: FirestoreTripDestination) => {
      const startDate = coerceDestinationDate(dest.startDate, rawStart);
      const endDate = coerceDestinationDate(dest.endDate, rawEnd ?? rawStart);
      if (!startDate || !endDate) {
        return null;
      }
      return {
        city: dest.city,
        country: dest.country,
        countryCode: dest.countryCode,
        startTime: dest.startTime,
        endTime: dest.endTime,
        locationId: dest.locationId,
        placeId: dest.placeId,
        startDate,
        endDate,
        coordinates: dest.coordinates,
      };
    })
    .filter((dest): dest is TripDestination => dest != null);

  const { startDate, endDate, destinations: reconciledDestinations } = reconcileTripDateFields(
    rawStart,
    rawEnd,
    destinations,
  );

  return {
    id,
    name: data.title,
    startDate,
    endDate,
    status: data.status,
    destination: data.destination, // Legacy field for backward compatibility
    destinations: reconciledDestinations, // New multi-city field
    travelingFrom: data.travelingFrom,
    travelMode: data.travelMode,
    preferredTravelMode: data.preferredTravelMode as TravelMode | undefined,
    emailSyncEnabled: data.emailSyncEnabled,
    landmarkImage: data.landmarkImage,
    landmarkImageCredit: data.landmarkImageCredit ?? null,
    landmarkImageCreditUrl: data.landmarkImageCreditUrl ?? null,
    owner: data.owner,
    ownerName: typeof data.ownerName === 'string' ? data.ownerName : undefined,
    members: data.members,
    memberIds: Array.isArray(data.memberIds) ? data.memberIds : undefined,
    collaborators,
    formerMembers,
    lastEditedBy: data.lastEditedBy,
    lastEditedAt: data.lastEditedAt instanceof Timestamp
      ? data.lastEditedAt.toDate()
      : typeof data.lastEditedAt === 'string'
        ? new Date(data.lastEditedAt)
        : data.lastEditedAt ? new Date() : undefined,
    budget: data.budget,
    locale: data.locale,
    baseCurrency: data.baseCurrency || 'USD',
    budgetCategoryAllocations: data.budgetCategoryAllocations,
    createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
    updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date(),
    isArchived: data.isArchived ?? false,
    budgetAlertThresholds:
      data.budgetAlertThresholds && data.budgetAlertThresholds.length > 0
        ? data.budgetAlertThresholds
        : DEFAULT_BUDGET_ALERT_THRESHOLDS,
    budgetAlertState: data.budgetAlertState,
    referencePoints: convertReferencePointsFromFirestore(data.referencePoints),
    primaryReferencePointId: data.primaryReferencePointId,
    distanceDisplayMode: data.distanceDisplayMode,
    walkingRadiusConfig: data.walkingRadiusConfig,
  };
};

// Fetch all trips for a user (owner OR collaborator)
// Supports cursor-based pagination for better performance
export const fetchUserTrips = async (
  uid: string,
  limitCount: number = 100,
  lastDoc?: QueryDocumentSnapshot
): Promise<{ trips: Trip[]; lastDoc?: QueryDocumentSnapshot }> => {
  let callableFallbackTrips: Trip[] | null = null;
  const ensureCallableFallbackTrips = async (): Promise<Trip[]> => {
    if (callableFallbackTrips) {
      return callableFallbackTrips;
    }
    callableFallbackTrips = await fetchAccessibleTripsViaCallable(uid, limitCount);
    return callableFallbackTrips;
  };

  try {
    // If offline, return cached trips
    if (!networkMonitor.isOnline()) {
      const { logger } = await import('@/services/logger');
      logger.debug('Device is offline, returning cached trips');
      const cachedTrips = await getCachedTrips(uid);
      updateLastKnownTrips(uid, cachedTrips);
      return { trips: cachedTrips };
    }

    await primeAuthTokenBeforeFirestoreReads(uid);

    // Optimized: Use orderBy for better query performance
    // Get trips where user is owner (with pagination and ordering)
    let ownerQuery = query(
      collection(db, 'trips'),
      where('owner', '==', uid),
      orderBy('startDate', 'asc'),
      firestoreLimit(limitCount)
    );

    // Get trips where user is a member (with pagination and ordering)
    let memberQuery = query(
      collection(db, 'trips'),
      where('members', 'array-contains', uid),
      orderBy('startDate', 'asc'),
      firestoreLimit(limitCount)
    );
    let memberIdsQuery = enableMemberIdsTripQuery
      ? query(
          collection(db, 'trips'),
          where('memberIds', 'array-contains', uid),
          orderBy('startDate', 'asc'),
          firestoreLimit(limitCount)
        )
      : null;

    // Apply cursor if provided
    if (lastDoc) {
      ownerQuery = query(ownerQuery, startAfter(lastDoc));
      memberQuery = query(memberQuery, startAfter(lastDoc));
      if (memberIdsQuery) {
        memberIdsQuery = query(memberIdsQuery, startAfter(lastDoc));
      }
    }

    const ownerPromise = getDocsPreferServer(ownerQuery);
    const memberPromise = getDocsPreferServer(memberQuery).then(
      (snap) => ({ docs: snap.docs, fromCache: snap.metadata.fromCache }),
      async (error) => {
        if (isOptionalTripMembershipQueryDenied(error)) {
          membershipListUnreliableByUser.add(uid);
          console.warn(
            '[Trips] Permission denied for member query; attempting listAccessibleTrips callable.',
          );
          await ensureCallableFallbackTrips();
          return { docs: [] as QueryDocumentSnapshot[], fromCache: false };
        }
        throw error;
      },
    );
    const memberIdsPromise =
      memberIdsQuery && !memberIdsQueryDeniedByUser.has(uid)
        ? getDocsPreferServer(memberIdsQuery).then(
            (snap) => ({ docs: snap.docs, fromCache: snap.metadata.fromCache }),
            async (error) => {
              if (isPermissionDeniedError(error)) {
                membershipListUnreliableByUser.add(uid);
                memberIdsQueryDeniedByUser.add(uid);
                console.warn(
                  '[Trips] Permission denied for memberIds query; attempting listAccessibleTrips callable.',
                );
                await ensureCallableFallbackTrips();
                return { docs: [] as QueryDocumentSnapshot[], fromCache: false };
              }
              throw error;
            },
          )
        : Promise.resolve({ docs: [] as QueryDocumentSnapshot[], fromCache: false });

    const [ownerSnapshot, memberOutcome, memberIdsOutcome] = await Promise.all([
      ownerPromise,
      memberPromise,
      memberIdsPromise,
    ]);
    const memberDocs = memberOutcome.docs;
    const memberIdsDocs = memberIdsOutcome.docs;
    const anySnapshotFromCache =
      ownerSnapshot.metadata.fromCache || memberOutcome.fromCache || memberIdsOutcome.fromCache;

    const tripsMap = new Map<string, Trip>();
    let lastDocument: QueryDocumentSnapshot | undefined;

    ownerSnapshot.forEach((doc) => {
      const data = doc.data() as FirestoreTrip;
      tripsMap.set(doc.id, convertFirestoreTrip(doc.id, data));
      lastDocument = doc;
    });

    memberDocs.forEach((doc) => {
      const data = doc.data() as FirestoreTrip;
      // Only add if user is not the owner (already handled by owner query)
      if (data.owner !== uid) {
        tripsMap.set(doc.id, convertFirestoreTrip(doc.id, data));
        lastDocument = doc;
      }
    });
    memberIdsDocs.forEach((doc) => {
      const data = doc.data() as FirestoreTrip;
      if (data.owner !== uid) {
        tripsMap.set(doc.id, convertFirestoreTrip(doc.id, data));
        lastDocument = doc;
      }
    });

    if (callableFallbackTrips?.length) {
      for (const trip of callableFallbackTrips) {
        tripsMap.set(trip.id, trip);
      }
    }

    let trips = Array.from(tripsMap.values());
    try {
      const diskTrips = await getCachedTrips(uid);
      if (diskTrips.length > 0) {
        trips = mergeServerTripsWithOfflineCache(trips, diskTrips, uid);
      }
    } catch {
      // Non-fatal: server list is still usable.
    }

    // Offline → online: Firestore may briefly return an empty merged result while reconnecting.
    // Persisting that clears the Trips tab until restart (cold start re-hydrates from disk).
    if (trips.length === 0 && networkMonitor.isOnline()) {
      const previous = getLastKnownTrips(uid)?.trips ?? [];
      const diskTrips = await getCachedTrips(uid);
      const fallback = diskTrips.length > 0 ? diskTrips : previous;
      const retain =
        fallback.length > 0 &&
        (anySnapshotFromCache || diskTrips.length > 0);
      if (retain) {
        const { logger } = await import('@/services/logger');
        const filteredFallback = filterTripsByUserAccess(uid, fallback);
        // Never resurrect shared trips from disk/session when the server returned an empty list
        // (common after leave/revoke with stale Firestore persistence on web).
        const safeFallback = filteredFallback.filter((trip) => trip.owner === uid);
        logger.warn('[Trips] Rejecting empty online trip list while session/disk still has trips', {
          snapshotsFromCache: anySnapshotFromCache,
          diskTrips: diskTrips.length,
          sessionTrips: previous.length,
          retainedOwnerTrips: safeFallback.length,
        });
        updateLastKnownTrips(uid, safeFallback);
        const sortedFallback = sortTripsByStartDateStable(safeFallback);
        return { trips: sortedFallback, lastDoc: lastDocument };
      }
    }

    // Auto-cache active trips
    await autoCacheActiveTrips(trips);
    const previousLastKnown = getLastKnownTrips(uid)?.trips ?? [];
    const recoveredIds = recoveredTripIdsByUser.get(uid);
    if (recoveredIds?.size) {
      const recoveredOnly = previousLastKnown.filter((trip) => trip.id && recoveredIds.has(trip.id));
      if (recoveredOnly.length) {
        trips = mergeTripsPreferNewerUpdatedAt(trips, recoveredOnly);
      }
    }
    trips = finalizeTripsForUser(uid, trips);
    updateLastKnownTrips(uid, trips);

    return { trips, lastDoc: lastDocument };
  } catch (error) {
    const isIndexRequiredError = (err: any): boolean => {
      const code = err?.code || err?.name;
      const msg = `${err?.message || ''}`;
      return (
        code === 'failed-precondition' ||
        code === 'FAILED_PRECONDITION' ||
        /requires an index/i.test(msg) ||
        /create the index/i.test(msg)
      );
    };

    // If we hit a composite-index issue (common on staging), retry with an un-ordered query.
    // This avoids blocking Maps (which uses this as a one-shot fallback) on missing indexes.
    if (isIndexRequiredError(error)) {
      try {
        const ownerFallback = query(
          collection(db, 'trips'),
          where('owner', '==', uid),
          firestoreLimit(limitCount),
        );
        const memberFallback = query(
          collection(db, 'trips'),
          where('members', 'array-contains', uid),
          firestoreLimit(limitCount),
        );
        const memberIdsFallback = enableMemberIdsTripQuery
          ? query(
              collection(db, 'trips'),
              where('memberIds', 'array-contains', uid),
              firestoreLimit(limitCount),
            )
          : null;

        const ownerFbPromise = getDocsPreferServer(ownerFallback);
        const memberFbPromise = getDocsPreferServer(memberFallback).then(
          (snap) => ({ docs: snap.docs }),
          async (error) => {
            if (isOptionalTripMembershipQueryDenied(error)) {
              membershipListUnreliableByUser.add(uid);
              console.warn(
                '[Trips] Permission denied for member fallback query; attempting listAccessibleTrips callable.',
              );
              await ensureCallableFallbackTrips();
              return { docs: [] as QueryDocumentSnapshot[] };
            }
            throw error;
          },
        );
        const memberIdsFbPromise =
          memberIdsFallback && !memberIdsQueryDeniedByUser.has(uid)
            ? getDocsPreferServer(memberIdsFallback).then(
                (snap) => ({ docs: snap.docs }),
                async (error) => {
                  if (isPermissionDeniedError(error)) {
                    membershipListUnreliableByUser.add(uid);
                    memberIdsQueryDeniedByUser.add(uid);
                    console.warn(
                      '[Trips] Permission denied for memberIds fallback query; attempting listAccessibleTrips callable.',
                    );
                    await ensureCallableFallbackTrips();
                    return { docs: [] as QueryDocumentSnapshot[] };
                  }
                  throw error;
                },
              )
            : Promise.resolve({ docs: [] as QueryDocumentSnapshot[] });

        const [ownerSnapshot, memberFbOutcome, memberIdsFbOutcome] = await Promise.all([
          ownerFbPromise,
          memberFbPromise,
          memberIdsFbPromise,
        ]);
        const memberDocs = memberFbOutcome.docs;
        const memberIdsDocs = memberIdsFbOutcome.docs;

        const tripsMap = new Map<string, Trip>();
        let lastDocument: QueryDocumentSnapshot | undefined;

        ownerSnapshot.forEach((doc) => {
          const data = doc.data() as FirestoreTrip;
          tripsMap.set(doc.id, convertFirestoreTrip(doc.id, data));
          lastDocument = doc;
        });

        memberDocs.forEach((doc) => {
          const data = doc.data() as FirestoreTrip;
          if (data.owner !== uid) {
            tripsMap.set(doc.id, convertFirestoreTrip(doc.id, data));
            lastDocument = doc;
          }
        });
        memberIdsDocs.forEach((doc) => {
          const data = doc.data() as FirestoreTrip;
          if (data.owner !== uid) {
            tripsMap.set(doc.id, convertFirestoreTrip(doc.id, data));
            lastDocument = doc;
          }
        });

        if (callableFallbackTrips?.length) {
          for (const trip of callableFallbackTrips) {
            tripsMap.set(trip.id, trip);
          }
        }

        let trips = sortTripsByStartDateStable(Array.from(tripsMap.values()));
        try {
          const diskTrips = await getCachedTrips(uid);
          if (diskTrips.length > 0) {
            trips = mergeServerTripsWithOfflineCache(trips, diskTrips, uid);
          }
        } catch {
          // ignore
        }

        await autoCacheActiveTrips(trips);
        const previousLastKnown = getLastKnownTrips(uid)?.trips ?? [];
        trips = mergeTripsPreferNewerUpdatedAt(trips, previousLastKnown);
        updateLastKnownTrips(uid, trips);
        return { trips, lastDoc: lastDocument };
      } catch {
        // fall through to the normal error handling below
      }
    }

    const { logger } = await import('@/services/logger');
    logger.error('Error fetching trips', error);

    // If online fetch fails, try to return cached trips
    if (networkMonitor.isOnline()) {
      logger.warn('Online fetch failed, falling back to cached trips');
      const cachedTrips = await getCachedTrips(uid);
      updateLastKnownTrips(uid, cachedTrips);
      return { trips: cachedTrips };
    }

    return { trips: [] };
  }
};

/**
 * Refetch trips after a failed optimistic delete so listeners match Firestore again.
 */
export async function recoverTripListAfterFailedDelete(uid: string): Promise<Trip[]> {
  return refreshTripListFromServer(uid);
}

export const getTripById = async (
  tripId: string,
  options?: { fromServer?: boolean },
): Promise<Trip | null> => {
  try {
    if (!tripId) return null;
    const tripRef = doc(db, 'trips', tripId);
    let snapshot;
    const preferServer = options?.fromServer === true;
    if (preferServer && networkMonitor.isOnline()) {
      try {
        snapshot = await getDocFromServer(tripRef);
      } catch {
        snapshot = await getDoc(tripRef);
      }
    } else {
      snapshot = await getDoc(tripRef);
    }
    if (!snapshot.exists()) return null;
    const data = snapshot.data() as FirestoreTrip;
    return convertFirestoreTrip(snapshot.id, data);
  } catch (error) {
    const { logger } = await import('@/services/logger');
    logger.warn('[Trips] getTripById failed', error as any);
    return null;
  }
};

function tripUpdatedAtMs(trip: Pick<Trip, 'updatedAt'>): number {
  const raw = trip.updatedAt;
  if (raw instanceof Date) {
    return raw.getTime();
  }
  if (typeof raw === 'string' || typeof raw === 'number') {
    const parsed = new Date(raw).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

/** Prefer `lastEditedAt` when present — more reliable than client-side `updatedAt` cache drift. */
function tripFreshnessMs(trip: Pick<Trip, 'updatedAt' | 'lastEditedAt'>): number {
  let ms = tripUpdatedAtMs(trip);
  const lastEdited = trip.lastEditedAt;
  if (lastEdited instanceof Date) {
    ms = Math.max(ms, lastEdited.getTime());
  } else if (typeof lastEdited === 'string' || typeof lastEdited === 'number') {
    const parsed = new Date(lastEdited).getTime();
    if (Number.isFinite(parsed)) {
      ms = Math.max(ms, parsed);
    }
  }
  return ms;
}

function applyUpdateTripDataForLiveList(base: Trip, tripData: UpdateTripData, uid: string): Trip {
  const next: Trip = { ...base, lastEditedBy: uid, lastEditedAt: new Date(), updatedAt: new Date() };
  if (tripData.name !== undefined) next.name = tripData.name;
  if (tripData.destination !== undefined) next.destination = tripData.destination;
  if (tripData.startDate !== undefined) next.startDate = tripData.startDate;
  if (tripData.endDate !== undefined) next.endDate = tripData.endDate;
  if (tripData.destinations !== undefined) next.destinations = tripData.destinations;
  if (tripData.travelMode !== undefined) next.travelMode = tripData.travelMode;
  if (tripData.preferredTravelMode !== undefined) next.preferredTravelMode = tripData.preferredTravelMode;
  if (tripData.emailSyncEnabled !== undefined) next.emailSyncEnabled = tripData.emailSyncEnabled;
  if (tripData.budget !== undefined) next.budget = tripData.budget ?? undefined;
  if (tripData.baseCurrency !== undefined) next.baseCurrency = tripData.baseCurrency;
  if (tripData.budgetCategoryAllocations !== undefined) {
    next.budgetCategoryAllocations = tripData.budgetCategoryAllocations;
  }
  if (tripData.budgetAlertThresholds !== undefined) {
    next.budgetAlertThresholds = tripData.budgetAlertThresholds;
  }
  if (tripData.budgetAlertState !== undefined) next.budgetAlertState = tripData.budgetAlertState;
  if (tripData.referencePoints !== undefined) next.referencePoints = tripData.referencePoints;
  if (tripData.primaryReferencePointId !== undefined) {
    next.primaryReferencePointId = tripData.primaryReferencePointId;
  }
  if (tripData.distanceDisplayMode !== undefined) next.distanceDisplayMode = tripData.distanceDisplayMode;
  if (tripData.walkingRadiusConfig !== undefined) next.walkingRadiusConfig = tripData.walkingRadiusConfig;
  if (tripData.ownerName !== undefined) next.ownerName = tripData.ownerName;
  return next;
}

function serverTripReflectsDateEdits(serverTrip: Trip, tripData: UpdateTripData): boolean {
  if (tripData.startDate !== undefined && tripDateMs(serverTrip.startDate) !== tripDateMs(tripData.startDate)) {
    return false;
  }
  if (tripData.endDate !== undefined && tripDateMs(serverTrip.endDate) !== tripDateMs(tripData.endDate)) {
    return false;
  }
  return true;
}

/** When Firestore local cache lags, keep the trip row with the latest `updatedAt`. */
export function mergeTripsPreferNewerUpdatedAt(...tripLists: Trip[][]): Trip[] {
  const byId = new Map<string, Trip>();
  for (const list of tripLists) {
    for (const trip of list) {
      if (!trip?.id) {
        continue;
      }
      const existing = byId.get(trip.id);
      if (!existing) {
        byId.set(trip.id, reconcileTripRecord(trip));
      } else {
        byId.set(trip.id, mergeTwoTripRecords(existing, trip));
      }
    }
  }
  return sortTripsByStartDateStable(Array.from(byId.values()));
}

/**
 * Server list is authoritative for trips that exist on Firestore. Disk cache only fills
 * trips missing from the server response (offline-created rows). Prevents stale AsyncStorage
 * rows with a bumped client `updatedAt` from overwriting committed June dates with May.
 */
export function mergeServerTripsWithOfflineCache(
  serverTrips: Trip[],
  cacheTrips: Trip[],
  uid?: string,
): Trip[] {
  const byId = new Map<string, Trip>();
  for (const trip of serverTrips) {
    if (trip?.id) {
      byId.set(trip.id, reconcileTripRecord(trip));
    }
  }
  for (const trip of cacheTrips) {
    if (!trip?.id || byId.has(trip.id)) {
      continue;
    }
    // Never resurrect shared trips the server omitted — common after leave/revoke with stale disk.
    if (uid && trip.owner && trip.owner !== uid) {
      continue;
    }
    if (uid && !canUserAccessTripFromStoredData(uid, trip)) {
      continue;
    }
    byId.set(trip.id, reconcileTripRecord(trip));
  }
  return sortTripsByStartDateStable(Array.from(byId.values()));
}

/** Keep nested `destinations[]` aligned when only top-level trip dates change. */
function applyDestinationDateSyncToFirestoreUpdates(
  firestoreUpdates: Record<string, unknown>,
  existingData: FirestoreTrip,
  startDate?: Date | null,
  endDate?: Date | null,
): void {
  if (startDate == null && endDate == null) {
    return;
  }
  const existing = existingData.destinations;
  if (!Array.isArray(existing) || existing.length === 0) {
    const resolvedStart = startDate ?? endDate;
    const resolvedEnd = endDate ?? startDate;
    if (resolvedStart && resolvedEnd && typeof existingData.destination === 'string') {
      const city = existingData.destination.split(',')[0]?.trim() || existingData.destination;
      firestoreUpdates.destinations = [
        {
          city,
          startDate: timestampFromLocalDateOnly(resolvedStart),
          endDate: timestampFromLocalDateOnly(resolvedEnd),
        },
      ];
    }
    return;
  }
  if (existing.length !== 1) {
    return;
  }
  const dest = existing[0];
  const synced: FirestoreTripDestination = {
    city: dest.city,
    startDate:
      startDate != null
        ? timestampFromLocalDateOnly(startDate)
        : dest.startDate instanceof Timestamp
          ? dest.startDate
          : timestampFromLocalDateOnly(new Date(dest.startDate as string)),
    endDate:
      endDate != null
        ? timestampFromLocalDateOnly(endDate)
        : dest.endDate instanceof Timestamp
          ? dest.endDate
          : timestampFromLocalDateOnly(new Date(dest.endDate as string)),
  };
  if (dest.country) synced.country = dest.country;
  if (dest.countryCode) synced.countryCode = dest.countryCode;
  if (dest.startTime) synced.startTime = dest.startTime;
  if (dest.endTime) synced.endTime = dest.endTime;
  if (dest.locationId) synced.locationId = dest.locationId;
  if (dest.placeId) synced.placeId = dest.placeId;
  if (dest.coordinates) synced.coordinates = dest.coordinates;
  firestoreUpdates.destinations = [synced];
}

function setTripInMapIfNewer(map: Map<string, Trip>, trip: Trip): void {
  const reconciled = reconcileTripRecord(trip);
  const existing = map.get(reconciled.id);
  if (!existing) {
    map.set(reconciled.id, reconciled);
    return;
  }
  map.set(reconciled.id, mergeTwoTripRecords(existing, reconciled));
}

/** Client-side sort for trip lists; avoids crashes when `startDate` is a string or invalid (e.g. cache hydration). */
function sortTripsByStartDateStable(trips: Trip[]): Trip[] {
  const startMs = (t: Trip): number => {
    const raw = t?.startDate as unknown;
    if (raw instanceof Date) {
      const ms = raw.getTime();
      return Number.isFinite(ms) ? ms : 0;
    }
    if (
      raw &&
      typeof raw === 'object' &&
      'toDate' in (raw as object) &&
      typeof (raw as { toDate?: () => Date }).toDate === 'function'
    ) {
      try {
        const ms = (raw as Timestamp).toDate().getTime();
        return Number.isFinite(ms) ? ms : 0;
      } catch {
        return 0;
      }
    }
    try {
      const ms = new Date(raw as string | number).getTime();
      return Number.isFinite(ms) ? ms : 0;
    } catch {
      return 0;
    }
  };
  return [...trips].sort((a, b) => startMs(a) - startMs(b));
}

function finalizeTripsForUser(uid: string, trips: Trip[]): Trip[] {
  return sortTripsByStartDateStable(filterTripsByUserAccess(uid, trips));
}

// Subscribe to real-time updates for user trips (owner OR collaborator)
export const subscribeToUserTrips = (
  uid: string,
  callback: (trips: Trip[]) => void,
  options?: {
    /**
     * Optional error hook for diagnosing cases where Firestore never calls the success callback.
     * Useful for surfacing "permission-denied"/network errors into screen-level diagnostics.
     */
    onError?: (error: unknown) => void;
    /**
     * Emit an initial callback even if Firestore hasn't responded yet.
     * Prevents the UI from getting stuck in a "no_callback" watchdog state.
     */
    bootstrapDelayMs?: number;
  }
): (() => void) => {
  logTripsSubscriptionVerbose('[subscribeToUserTrips] 🔵 Setting up subscription for uid:', uid);
  logTripsSubscriptionVerbose('[subscribeToUserTrips] 🔵 Firestore db instance:', db ? 'present' : 'MISSING');
  logTripsSubscriptionVerbose('[subscribeToUserTrips] 🔵 Timestamp:', new Date().toISOString());

  if (!enableRealtimeTripSubscriptions) {
    let isCleanedUp = false;
    const emitTrips = (incomingTrips: Trip[]) => {
      if (isCleanedUp) return;
      const previousLastKnown = getLastKnownTrips(uid)?.trips ?? [];
      const merged = mergeTripsPreferNewerUpdatedAt(incomingTrips, previousLastKnown);
      const sorted = finalizeTripsForUser(uid, mergeWithRecoverableLastKnownTrips(uid, merged));
      updateLastKnownTrips(uid, sorted);
      callback(sorted);
    };

    const onTripAccessRevoked = (_removedTripId: string) => {
      if (isCleanedUp) return;
      const trips = getLastKnownTrips(uid)?.trips ?? [];
      emitTrips(trips);
    };
    const unregisterPollAccessListener = registerLiveTripDeleter(uid, onTripAccessRevoked);
    const unregisterTripListReplay = registerTripListReplayHandler(uid, (nextTrips) => {
      emitTrips(nextTrips);
    });

    const poll = async () => {
      try {
        const { trips: serverTrips } = await fetchUserTrips(uid, 100);
        emitTrips(serverTrips);
      } catch (error) {
        if (!isCleanedUp) {
          options?.onError?.(error);
        }
      }
    };

    // Start server fetch immediately. Disk cache is only a placeholder until the first poll returns —
    // showing stale trip dates (e.g. pre-invite) caused collaborators to see the wrong trip instance.
    void poll();

    const lastKnown = (getLastKnownTrips(uid)?.trips ?? []).filter((trip) =>
      shouldHydrateTripFromSessionCache(uid, trip),
    );
    if (lastKnown.length > 0) {
      emitTrips(lastKnown);
    } else {
      void getCachedTrips(uid)
        .then((cached) => {
          if (!isCleanedUp && cached?.length && (getLastKnownTrips(uid)?.trips?.length ?? 0) === 0) {
            const seedTrips = cached.filter((trip) => shouldHydrateTripFromSessionCache(uid, trip));
            if (seedTrips.length) {
              emitTrips(seedTrips);
            }
          }
        })
        .catch((error) => {
          if (!isCleanedUp) {
            options?.onError?.(error);
          }
        });
    }
    const pollInterval = setInterval(() => {
      void poll();
    }, NATIVE_TRIPS_POLL_MS);

    return () => {
      isCleanedUp = true;
      unregisterPollAccessListener();
      unregisterTripListReplay();
      clearInterval(pollInterval);
    };
  }

  // Get trips where user is owner
  const ownerQuery = query(
    collection(db, 'trips'),
    where('owner', '==', uid)
  );
  logTripsSubscriptionVerbose('[subscribeToUserTrips] 🔵 Owner query created');

  // Get trips where user is a member (in members array)
  const memberQuery = query(
    collection(db, 'trips'),
    where('members', 'array-contains', uid)
  );
  const memberIdsQuery = enableMemberIdsTripQuery
    ? query(
        collection(db, 'trips'),
        where('memberIds', 'array-contains', uid)
      )
    : null;

  let ownerUnsubscribe: (() => void) | null = null;
  let memberUnsubscribe: (() => void) | null = null;
  let memberIdsUnsubscribe: (() => void) | null = null;
  let isCleanedUp = false;
  let hasCalledBack = false; // Track if we've ever called back
  let loggedFirstTripsEmit = false;
  const allTrips = new Map<string, Trip>();
  const ownerTripIds = new Set<string>();
  const memberTripIds = new Set<string>();
  const memberIdsTripIds = new Set<string>();
  const serverAuthoritativeTripIds = new Set<string>();
  let memberQueryHasEmitted = false;
  let memberIdsQueryHasEmitted = false;
  let memberQueryPermissionDenied = false;
  let memberIdsQueryPermissionDenied = false;

  const membershipQueriesReadyForStalePrune = (): boolean => {
    if (memberQueryPermissionDenied || memberIdsQueryPermissionDenied) {
      return false;
    }
    if (membershipListUnreliableByUser.has(uid)) {
      return false;
    }
    const memberReady = memberQueryHasEmitted;
    const memberIdsReady =
      !enableMemberIdsTripQuery ||
      memberIdsQueryHasEmitted ||
      memberIdsQueryDeniedByUser.has(uid);
    return memberReady && memberIdsReady;
  };

  const pruneStaleSharedTrips = (currentSharedIds: Set<string>) => {
    if (!membershipQueriesReadyForStalePrune()) {
      return;
    }
    const staleSharedTripIds: string[] = [];
    for (const [tripId, trip] of allTrips) {
      if (trip.owner !== uid && !currentSharedIds.has(tripId)) {
        staleSharedTripIds.push(tripId);
      }
    }
    for (const tripId of staleSharedTripIds) {
      allTrips.delete(tripId);
    }
  };

  // In-memory list from earlier successful snapshots this JS session (clears on cold start).
  for (const t of getLastKnownTrips(uid)?.trips ?? []) {
    if (shouldHydrateTripFromSessionCache(uid, t)) {
      setTripInMapIfNewer(allTrips, t);
    }
  }

  let bootstrapTimer: ReturnType<typeof setTimeout> | null = null;
  let fallbackTimer: ReturnType<typeof setTimeout> | null = null;
  let absoluteUnstickTimer: ReturnType<typeof setTimeout> | null = null;
  let subscriptionsAttached = false;

  const clearBootstrapAndFallbackTimers = () => {
    if (bootstrapTimer) {
      clearTimeout(bootstrapTimer);
      bootstrapTimer = null;
    }
    if (fallbackTimer) {
      clearTimeout(fallbackTimer);
      fallbackTimer = null;
    }
    if (absoluteUnstickTimer) {
      clearTimeout(absoluteUnstickTimer);
      absoluteUnstickTimer = null;
    }
  };

  /** Only sets `hasCalledBack` after the consumer runs without throwing. */
  const notifyConsumer = (tripsArray: Trip[]): boolean => {
    if (isCleanedUp) {
      return false;
    }
    try {
      logTripsSubscriptionVerbose(
        `[subscribeToUserTrips] Calling callback with ${tripsArray.length} trips:`,
        tripsArray.map((t) => ({ id: t.id, name: t.name, owner: t.owner })),
      );
      callback(tripsArray);
      if (!loggedFirstTripsEmit) {
        loggedFirstTripsEmit = true;
        addBreadcrumb('Trips list first consumer emit', 'trips.firestore', 'info', {
          tripCount: tripsArray.length,
        });
      }
      hasCalledBack = true;
      return true;
    } catch (err) {
      console.error('[subscribeToUserTrips] Consumer callback threw; will retry on next emit or fallback timer', err);
      return false;
    }
  };

  // IMPORTANT: Do not clear bootstrap/fallback timers until a consumer callback succeeds.
  // If we clear timers first and then the React setState in the consumer throws, nothing will
  // emit again and Trips can stay on skeletons forever.
  const updateCallback = () => {
    if (isCleanedUp) {
      return;
    }

    const tripsArray = finalizeTripsForUser(
      uid,
      mergeWithRecoverableLastKnownTrips(uid, Array.from(allTrips.values())),
    );
    updateLastKnownTrips(uid, tripsArray);

    if (!notifyConsumer(tripsArray)) {
      return;
    }
    clearBootstrapAndFallbackTimers();
    const ids = tripsArray.map((t) => t.id);
    void syncCacheWithTripIds(ids);
  };

  const emitSortedTripsSync = (): boolean => {
    const tripsArray = finalizeTripsForUser(
      uid,
      mergeWithRecoverableLastKnownTrips(uid, Array.from(allTrips.values())),
    );
    updateLastKnownTrips(uid, tripsArray);
    return notifyConsumer(tripsArray);
  };

  const pruneLiveDeletedTrip = (tripId: string) => {
    if (isCleanedUp) {
      return;
    }
    if (!tripId) {
      return;
    }
    if (!allTrips.delete(tripId)) {
      return;
    }
    ownerTripIds.delete(tripId);
    memberTripIds.delete(tripId);
    memberIdsTripIds.delete(tripId);
    updateCallback();
  };
  const unregisterLiveDeleter = registerLiveTripDeleter(uid, pruneLiveDeletedTrip);

  const unregisterTripListReplay = registerTripListReplayHandler(uid, (mergedTrips) => {
    if (isCleanedUp) return;
    applyAuthoritativeTripListToLiveMap(
      uid,
      allTrips,
      ownerTripIds,
      memberTripIds,
      memberIdsTripIds,
      mergedTrips,
      serverAuthoritativeTripIds,
    );
    updateCallback();
  });

  const attachSubscriptions = () => {
    if (subscriptionsAttached || isCleanedUp) return;
    subscriptionsAttached = true;

    logTripsSubscriptionVerbose('[subscribeToUserTrips] 🔵 Hydrated from disk; attaching listeners');

    // Emit once synchronously from merged in-memory + last-known cache so the Trips tab can clear
    // loading without waiting for setTimeout(0) or Firestore — important when the main thread is
    // busy right after navigation (e.g. web dev bundles + heavy tab layouts).
    if (!hasCalledBack && !isCleanedUp) {
      const ok = emitSortedTripsSync() || notifyConsumer([]);
      if (ok) {
        clearBootstrapAndFallbackTimers();
      }
    }

    // Emit at least one callback quickly — now includes disk-backed trips so cold-start offline works.
    const bootstrapDelayMs = options?.bootstrapDelayMs ?? 0;
    if (!hasCalledBack) {
      bootstrapTimer = setTimeout(() => {
        if (isCleanedUp) return;
        if (hasCalledBack) return;

        logTripsSubscriptionVerbose('[subscribeToUserTrips] 🔵 Bootstrap timer firing (synchronous path)');
        const ok = emitSortedTripsSync() || notifyConsumer([]);
        if (ok) {
          clearBootstrapAndFallbackTimers();
        }
        bootstrapTimer = null;
      }, bootstrapDelayMs);
    }

    // Fallback timer: guarantee at least one callback within 3 seconds
    if (!hasCalledBack) {
      fallbackTimer = setTimeout(() => {
        if (!hasCalledBack && !isCleanedUp) {
          console.warn('[subscribeToUserTrips] Firestore did not respond within 3s, invoking callback with current data');
          addBreadcrumb('Trips Firestore 3s fallback emit', 'trips.firestore', 'warning', {
            mergedTripCount: allTrips.size,
          });
          const ok = emitSortedTripsSync() || notifyConsumer([]);
          if (ok) {
            clearBootstrapAndFallbackTimers();
          }
        }
        fallbackTimer = null;
      }, 3000);
    }

    logTripsSubscriptionVerbose('[subscribeToUserTrips] 🔵 About to attach onSnapshot for owner query...');
  const snapshotStart = Date.now();
  try {
    ownerUnsubscribe = onSnapshot(ownerQuery, (snapshot) => {
      logTripsSubscriptionVerbose(`[subscribeToUserTrips] ✅ Owner query callback FIRED! Docs: ${snapshot.size}`);
      // Don't process updates if already cleaned up
      if (isCleanedUp) {
        logTripsSubscriptionVerbose('[subscribeToUserTrips] ⚠️ Owner callback after cleanup, ignoring');
        return;
      }

      // Track current owner trip IDs
      const currentOwnerIds = new Set<string>();

      snapshot.forEach((doc) => {
        const data = doc.data() as FirestoreTrip;
        currentOwnerIds.add(doc.id);
        try {
          setTripInMapIfNewer(allTrips, convertFirestoreTrip(doc.id, data));
        } catch (error) {
          console.error(`Error converting owner trip ${doc.id}:`, error);
        }
      });

      logTripsSubscriptionVerbose(`[subscribeToUserTrips] Owner query: ${snapshot.size} trips for uid ${uid}`);

      // Remove trips that are no longer owned by this user
      ownerTripIds.forEach((tripId) => {
        if (!currentOwnerIds.has(tripId)) {
          const trip = allTrips.get(tripId);
          // Only remove if user is the owner (member query will keep it if user is a member)
          if (trip && trip.owner === uid) {
            allTrips.delete(tripId);
          }
        }
      });

      // Update tracked owner IDs
      ownerTripIds.clear();
      currentOwnerIds.forEach(id => ownerTripIds.add(id));

      updateCallback();
    }, (error) => {
      // Don't process errors if already cleaned up
      if (isCleanedUp) {
        return;
      }
      console.error('[subscribeToUserTrips] ❌ Owner query ERROR:', error);
      console.error('[subscribeToUserTrips] ❌ Error code:', (error as any)?.code);
      console.error('[subscribeToUserTrips] ❌ Error message:', (error as any)?.message);
      options?.onError?.(error);
      addBreadcrumb('Owner trips onSnapshot error', 'trips.firestore', 'error', {
        code: String((error as { code?: string })?.code ?? ''),
      });

      // For permission errors, detach only the failing owner stream and keep the
      // subscription alive. This avoids wiping the UI to an empty trip list when
      // cached/member data is still available.
      const errorCode = (error as any)?.code as string | undefined;
      if (errorCode === 'permission-denied' || errorCode === 'missing-or-insufficient-permissions') {
        console.warn(
          '[Trips] Permission denied for owner query. If Firestore App Check enforcement is on, the web client must initialize App Check (site key + Enterprise provider). Continuing with member/memberIds + cached trips.',
        );
        addBreadcrumb('Owner trips permission denied', 'trips.firestore', 'error', { code: errorCode });
        if (ownerUnsubscribe) {
          try {
            ownerUnsubscribe();
          } catch (unsubError) {
            // Ignore cleanup errors
          }
          ownerUnsubscribe = null;
        }
        // Emit current merged state (if any) so screens don't drop to empty.
        try {
          updateCallback();
        } catch (err) {
          console.error('[subscribeToUserTrips] Consumer callback threw after owner permission-denied update', err);
        }
        return;
      }

      // Don't clear all trips on error - just log and continue with what we have
      // This prevents other errors from clearing all trips
      // Ensure the consumer is not left hanging in a loading state.
      try {
        updateCallback();
      } catch (callbackError) {
        // As a last resort, call back synchronously with current trips.
        try {
          callback(sortTripsByStartDateStable(Array.from(allTrips.values())));
        } catch {
          callback([]);
        }
      }
    });
    const snapshotDuration = Date.now() - snapshotStart;
    logTripsSubscriptionVerbose(`[subscribeToUserTrips] 🔵 onSnapshot setup took ${snapshotDuration}ms`);
  } catch (error) {
    console.error('[subscribeToUserTrips] Error setting up owner subscription:', error);
    options?.onError?.(error);
    addBreadcrumb('Owner trips subscription setup failed', 'trips.firestore', 'error');
    ownerUnsubscribe = null;
  }

  logTripsSubscriptionVerbose('[subscribeToUserTrips] 🔵 About to attach onSnapshot for member query...');
  try {
    memberUnsubscribe = onSnapshot(memberQuery, (snapshot) => {
      logTripsSubscriptionVerbose('[subscribeToUserTrips] ✅ Member query callback FIRED! Docs:', snapshot.size);
      // Don't process updates if already cleaned up
      if (isCleanedUp) {
        logTripsSubscriptionVerbose('[subscribeToUserTrips] ⚠️ Member callback after cleanup, ignoring');
        return;
      }

      // Track current member trip IDs
      const currentMemberIds = new Set<string>();

      snapshot.forEach((doc) => {
        const data = doc.data() as FirestoreTrip;
        currentMemberIds.add(doc.id);
        // IMPORTANT:
        // Do NOT skip trips where `data.owner === uid`.
        //
        // In theory, owner trips are "already handled" by the owner query, but in practice the owner query
        // can be temporarily empty (cache cold start), delayed, or fail (permissions / flaky network).
        // If we skip here, the merged list can incorrectly become empty and the UI shows "No trips yet".
        //
        // Using a Map keyed by tripId already de-dupes, so it is safe to always set.
        try {
          const trip = convertFirestoreTrip(doc.id, data);
          if (shouldSkipStaleSharedTripSnapshot(trip, snapshot.metadata.fromCache, serverAuthoritativeTripIds)) {
            return;
          }
          setTripInMapIfNewer(allTrips, trip);
          logTripsSubscriptionVerbose(
            `[subscribeToUserTrips] Member query trip: ${doc.id} (${trip.name}), owner: ${trip.owner}, members:`,
            data.members,
          );
        } catch (error) {
          console.error(`Error converting member trip ${doc.id}:`, error);
        }
      });

      logTripsSubscriptionVerbose(`[subscribeToUserTrips] Member query: ${snapshot.size} trips for uid ${uid}`, {
        tripIds: Array.from(currentMemberIds),
        allTripsCount: allTrips.size,
        uid,
      });

      memberQueryHasEmitted = true;
      const currentSharedIds = new Set<string>([
        ...currentMemberIds,
        ...memberIdsTripIds,
      ]);
      pruneStaleSharedTrips(currentSharedIds);

      // Update tracked member IDs
      memberTripIds.clear();
      currentMemberIds.forEach(id => memberTripIds.add(id));

      updateCallback();
    }, (error) => {
      // Don't process errors if already cleaned up
      if (isCleanedUp) {
        return;
      }

      // For permission errors, clean up but don't fail the whole subscription
      // The owner query might still work
      const memberErrorCode = (error as any)?.code as string | undefined;
      if (memberErrorCode === 'permission-denied' || memberErrorCode === 'missing-or-insufficient-permissions') {
        memberQueryPermissionDenied = true;
        membershipListUnreliableByUser.add(uid);
        console.warn(
          '[Trips] Permission denied for member query; attempting listAccessibleTrips callable.',
        );
        addBreadcrumb('Member trips permission denied', 'trips.firestore', 'warning', { code: memberErrorCode });
        if (memberUnsubscribe) {
          try {
            memberUnsubscribe();
          } catch (unsubError) {
            // Ignore cleanup errors
          }
          memberUnsubscribe = null;
        }
        void fetchAccessibleTripsViaCallable(uid, 100)
          .then((callableTrips) => {
            if (isCleanedUp) {
              return;
            }
            mergeCallableTripsIntoLiveSubscription(
              uid,
              allTrips,
              ownerTripIds,
              memberTripIds,
              memberIdsTripIds,
              callableTrips,
              serverAuthoritativeTripIds,
            );
            updateCallback();
          })
          .catch((err) => {
            console.error('[Trips] Callable fallback after member query permission-denied failed', err);
            updateCallback();
          });
        return;
      }

      console.error('Error in member trips subscription:', error);
      options?.onError?.(error);
      addBreadcrumb('Member trips onSnapshot error', 'trips.firestore', 'error', {
        code: String((error as { code?: string })?.code ?? ''),
      });

      // Don't clear all trips on error - member query might fail if user has no shared trips
      // or if permission rules don't allow the query. Continue with trips from owner query.
      console.warn(
        'Member trips query failed (this is OK if you have no shared trips or permission issues). Continuing with owner trips only.',
      );
      // Ensure the consumer is not left hanging in a loading state.
      try {
        updateCallback();
      } catch (callbackError) {
        try {
          callback(sortTripsByStartDateStable(Array.from(allTrips.values())));
        } catch {
          callback([]);
        }
      }
    });
  } catch (error) {
    console.error('[subscribeToUserTrips] Error setting up member subscription:', error);
    options?.onError?.(error);
    addBreadcrumb('Member trips subscription setup failed', 'trips.firestore', 'error');
    memberUnsubscribe = null;
  }

  if (memberIdsQuery && !memberIdsQueryDeniedByUser.has(uid)) {
    logTripsSubscriptionVerbose('[subscribeToUserTrips] 🔵 About to attach onSnapshot for memberIds query...');
    try {
      memberIdsUnsubscribe = onSnapshot(memberIdsQuery, (snapshot) => {
      if (isCleanedUp) {
        logTripsSubscriptionVerbose('[subscribeToUserTrips] ⚠️ memberIds callback after cleanup, ignoring');
        return;
      }

      const currentMemberIdsDocs = new Set<string>();
      snapshot.forEach((doc) => {
        const data = doc.data() as FirestoreTrip;
        currentMemberIdsDocs.add(doc.id);
        try {
          const trip = convertFirestoreTrip(doc.id, data);
          if (shouldSkipStaleSharedTripSnapshot(trip, snapshot.metadata.fromCache, serverAuthoritativeTripIds)) {
            return;
          }
          setTripInMapIfNewer(allTrips, trip);
        } catch (error) {
          console.error(`Error converting memberIds trip ${doc.id}:`, error);
        }
      });

      memberIdsQueryHasEmitted = true;
      const currentSharedIds = new Set<string>([
        ...memberTripIds,
        ...currentMemberIdsDocs,
      ]);
      pruneStaleSharedTrips(currentSharedIds);

      memberIdsTripIds.clear();
      currentMemberIdsDocs.forEach((id) => memberIdsTripIds.add(id));
      updateCallback();
      }, (error) => {
        if (isCleanedUp) {
          return;
        }
        const memberIdsErrorCode = (error as any)?.code as string | undefined;
        if (memberIdsErrorCode === 'permission-denied' || memberIdsErrorCode === 'missing-or-insufficient-permissions') {
          memberIdsQueryPermissionDenied = true;
          membershipListUnreliableByUser.add(uid);
          memberIdsQueryDeniedByUser.add(uid);
          console.warn(
            '[Trips] Permission denied for memberIds query; attempting listAccessibleTrips callable.',
          );
          if (memberIdsUnsubscribe) {
            try {
              memberIdsUnsubscribe();
            } catch {
              // ignore cleanup errors
            }
            memberIdsUnsubscribe = null;
          }
          void fetchAccessibleTripsViaCallable(uid, 100)
            .then((callableTrips) => {
              if (isCleanedUp) {
                return;
              }
              mergeCallableTripsIntoLiveSubscription(
                uid,
                allTrips,
                ownerTripIds,
                memberTripIds,
                memberIdsTripIds,
                callableTrips,
                serverAuthoritativeTripIds,
              );
              updateCallback();
            })
            .catch((err) => {
              console.error('[Trips] Callable fallback after memberIds query permission-denied failed', err);
              updateCallback();
            });
          return;
        }
        console.error('Error in memberIds trips subscription:', error);
        options?.onError?.(error);
        try {
          updateCallback();
        } catch {
          try {
            callback(sortTripsByStartDateStable(Array.from(allTrips.values())));
          } catch {
            callback([]);
          }
        }
      });
    } catch (error) {
      console.error('[subscribeToUserTrips] Error setting up memberIds subscription:', error);
      options?.onError?.(error);
      memberIdsUnsubscribe = null;
    }
  } else {
    logTripsSubscriptionVerbose('[subscribeToUserTrips] memberIds stream skipped (permission-denied earlier this session)');
  }
  };

  let hydrationDone = false;

  const finishHydration = () => {
    if (hydrationDone) return;
    hydrationDone = true;
    if (isCleanedUp) return;
    void (async () => {
      try {
        await primeAuthTokenBeforeFirestoreReads(uid);
      } catch (err) {
        console.warn('[subscribeToUserTrips] Auth token prime before snapshots failed', err);
      }
      if (isCleanedUp) {
        return;
      }
      attachSubscriptions();
      // Force a server-backed list after cold start so stale disk / Firestore persistence cannot
      // keep pre-invite dates (native polls every 15s; web uses onSnapshot + this one-shot).
      void refreshTripListFromServer(uid).catch((err) => {
        console.warn('[subscribeToUserTrips] Server trip list bootstrap failed', err);
      });
    })();

    // Last-resort: if every notifyConsumer attempt failed (e.g. React setState threw) or Firestore
    // never delivered, still emit once so Trips tab cannot stay on skeletons forever.
    const ABSOLUTE_UNSTICK_MS = 10_000;
    absoluteUnstickTimer = setTimeout(() => {
      absoluteUnstickTimer = null;
      if (isCleanedUp || hasCalledBack) {
        return;
      }
      console.warn(
        `[subscribeToUserTrips] ${ABSOLUTE_UNSTICK_MS}ms safety: forcing trip list emit (check Firestore rules, network, or prior setState errors).`,
      );
      addBreadcrumb('Trips Firestore 10s absolute unstick emit', 'trips.firestore', 'warning', {
        mergedTripCount: allTrips.size,
      });
      try {
        const tripsArray = finalizeTripsForUser(uid, Array.from(allTrips.values()));
        updateLastKnownTrips(uid, tripsArray);
        callback(tripsArray);
        hasCalledBack = true;
      } catch (e) {
        try {
          callback([]);
          hasCalledBack = true;
        } catch {
          // ignore
        }
      }
      clearBootstrapAndFallbackTimers();
    }, ABSOLUTE_UNSTICK_MS);
  };

  // Cold start: merge disk-backed trips before the first consumer emit so we don't flash an empty
  // list (or stale "archived only" cached rows alone) while Firestore catches up. Cap wait so slow
  // disk cannot block snapshots for long.
  const DISK_PREHYDRATE_BUDGET_MS = 450;
  void (async () => {
    if ((getLastKnownTrips(uid)?.trips ?? []).length === 0) {
      try {
        const cached = await Promise.race([
          getCachedTrips(uid),
          new Promise<undefined>((resolve) =>
            setTimeout(() => resolve(undefined), DISK_PREHYDRATE_BUDGET_MS),
          ),
        ]);
        if (!isCleanedUp && cached?.length) {
          for (const t of cached) {
            if (shouldHydrateTripFromSessionCache(uid, t)) {
              setTripInMapIfNewer(allTrips, t);
            }
          }
        }
      } catch (err) {
        console.warn('[subscribeToUserTrips] getCachedTrips pre-hydrate failed:', err);
      }
    }
    if (!isCleanedUp) {
      finishHydration();
    }
  })();

  void getCachedTrips(uid)
    .then((cached) => {
      if (isCleanedUp) return;
      if (!cached?.length) return;
      for (const t of cached) {
        if (shouldHydrateTripFromSessionCache(uid, t)) {
          setTripInMapIfNewer(allTrips, t);
        }
      }
      updateCallback();
    })
    .catch((err) => {
      console.warn('[subscribeToUserTrips] getCachedTrips hydrate failed:', err);
    });

  const emitTerminalIfNeeded = () => {
    if (hasCalledBack) {
      return;
    }
    try {
      const tripsArray = finalizeTripsForUser(uid, Array.from(allTrips.values()));
      updateLastKnownTrips(uid, tripsArray);
      if (!notifyConsumer(tripsArray)) {
        notifyConsumer([]);
      }
    } catch {
      try {
        notifyConsumer([]);
      } catch {
        // ignore
      }
    }
  };

  return () => {
    if (isCleanedUp) {
      return; // Already cleaned up, prevent double cleanup
    }

    // If teardown happens before getCachedTrips + finishHydration() attach listeners (Spotlight
    // peek, scene invalidation, Strict Mode), we clear hydrationTimeout and never emit — Trips
    // stays loading until a lucky resubscribe. Emit once with memory + any merged cache rows.
    emitTerminalIfNeeded();

    isCleanedUp = true;
    unregisterLiveDeleter();
    unregisterTripListReplay();

    clearBootstrapAndFallbackTimers();

    if (ownerUnsubscribe) {
      try {
        ownerUnsubscribe();
      } catch (error) {
        // Silently ignore cleanup errors - they're often due to Firestore internal state
      } finally {
        ownerUnsubscribe = null; // Clear reference after cleanup
      }
    }

    if (memberUnsubscribe) {
      try {
        memberUnsubscribe();
      } catch (error) {
        // Silently ignore cleanup errors - they're often due to Firestore internal state
      } finally {
        memberUnsubscribe = null; // Clear reference after cleanup
      }
    }
    if (memberIdsUnsubscribe) {
      try {
        memberIdsUnsubscribe();
      } catch (error) {
        // Silently ignore cleanup errors - they're often due to Firestore internal state
      } finally {
        memberIdsUnsubscribe = null;
      }
    }
  };
};

const sanitizeRecord = <T extends Record<string, any>>(record: T): T => {
  const entries = Object.entries(record).filter(([, v]) => v !== undefined);
  return Object.fromEntries(entries) as T;
};

const buildDestinationQuery = (destination?: TripDestination | null): string | undefined => {
  if (!destination?.city) return undefined;
  const city = destination.city.trim();
  const country = destination.country?.trim();
  if (!city) return undefined;
  return country ? `${city}, ${country}` : city;
};

const normalizeDestinationForCompare = (value?: string | null): string =>
  (value || '').trim().toLowerCase();

const hasDestinationChanged = (next?: string | null, previous?: string | null): boolean => {
  const normalizedNext = normalizeDestinationForCompare(next);
  if (!normalizedNext) return false;
  return normalizedNext !== normalizeDestinationForCompare(previous);
};

const resolveDestinationIdentity = (destination?: TripDestination | null): string | undefined => {
  if (!destination) return undefined;
  return destination.placeId || destination.locationId || undefined;
};

const triggerDestinationLandmarkPrewarm = (destination?: string, countryCode?: string): void => {
  const city = (destination || '').trim();
  if (!city) return;
  import('./places')
    .then(({ requestLandmarkCachePrewarm }) =>
      requestLandmarkCachePrewarm(city, { countryCode }),
    )
    .catch((error) => {
      if (__DEV__) {
        console.warn('[trips] Failed to request landmark cache prewarm', { city, error });
      }
    });
};

// Create a new trip
export const createTrip = async (uid: string, tripData: CreateTripData): Promise<string> => {
  // Check if this is the user's first trip (before creating the new one)
  const existingTripsQuery = query(
    collection(db, 'trips'),
    where('owner', '==', uid),
    orderBy('createdAt', 'desc'),
    firestoreLimit(1)
  );
  const existingTripsSnapshot = await getDocs(existingTripsQuery);
  const isFirstTrip = existingTripsSnapshot.empty;

  // Determine destination(s) - prefer destinations array, fallback to single destination
  const primaryDestination = tripData.destinations && tripData.destinations.length > 0
    ? tripData.destinations[0]
    : undefined;
  const finalDestination = buildDestinationQuery(primaryDestination) || tripData.destination || '';

  // Get landmark image for the primary destination.
  // NOTE: This can be slow (network + caching). Allow skipping for fast-create flows.
  const landmarkImagePromise = tripData.skipLandmarkImage
    ? Promise.resolve(null)
    : resolveLandmarkImageResult(finalDestination, {
      identityKey: resolveDestinationIdentity(primaryDestination),
      countryName: primaryDestination?.country,
    });

  const createTripCallable = httpsCallable<
    {
      name: string;
      destination?: string;
      startDate: string;
      endDate: string;
      travelMode?: string;
      emailSyncEnabled?: boolean;
    },
    { tripId: string }
  >(functions, 'createTripWithEntitlement');

  const normalizedDestination = typeof finalDestination === 'string' && finalDestination.trim().length > 0
    ? finalDestination.trim()
    : undefined;
  const normalizedTravelMode = typeof tripData.travelMode === 'string' && tripData.travelMode.trim().length > 0
    ? tripData.travelMode.trim()
    : undefined;

  const createPayload = {
    name: tripData.name,
    ...(normalizedDestination ? { destination: normalizedDestination } : {}),
    startDate: tripData.startDate.toISOString(),
    endDate: tripData.endDate.toISOString(),
    ...(normalizedTravelMode ? { travelMode: normalizedTravelMode } : {}),
    emailSyncEnabled: tripData.emailSyncEnabled ?? false,
  };

  const createResult = await createTripCallable(createPayload);
  const createdTripId = createResult.data?.tripId;
  if (!createdTripId) {
    throw new Error('Failed to create trip. Missing trip id from server.');
  }
  const createdTripRef = doc(db, 'trips', createdTripId);

  // Save optional fields that are not part of the minimal callable payload.
  const enrichmentPayload: Record<string, unknown> = {};
  if (tripData.preferredTravelMode !== undefined) {
    enrichmentPayload.preferredTravelMode = tripData.preferredTravelMode;
  }
  if (tripData.destinations && tripData.destinations.length > 0) {
    enrichmentPayload.destinations = serializeTripDestinationsForWrite(tripData.destinations);
  }
  if (tripData.travelingFrom) {
    enrichmentPayload.travelingFrom = tripData.travelingFrom;
  }
  if (tripData.referencePoints && tripData.referencePoints.length > 0) {
    enrichmentPayload.referencePoints = serializeReferencePointsForWrite(tripData.referencePoints);
  }
  if (tripData.primaryReferencePointId) {
    enrichmentPayload.primaryReferencePointId = tripData.primaryReferencePointId;
  }
  if (tripData.distanceDisplayMode) {
    enrichmentPayload.distanceDisplayMode = tripData.distanceDisplayMode;
  }
  if (tripData.walkingRadiusConfig) {
    enrichmentPayload.walkingRadiusConfig = tripData.walkingRadiusConfig;
  }
  if (tripData.baseCurrency) {
    enrichmentPayload.baseCurrency = tripData.baseCurrency;
  }
  if (tripData.budgetUpdateMode) {
    enrichmentPayload.budgetUpdateMode = tripData.budgetUpdateMode;
  }

  if (Object.keys(enrichmentPayload).length > 0) {
    await setDoc(
      createdTripRef,
      {
        ...enrichmentPayload,
        updatedAt: serverTimestamp(),
        lastEditedAt: serverTimestamp(),
      },
      { merge: true },
    );
  }

  // Determine creation method once for all trip creation analytics.
  let creationMethod: 'manual' | 'ai' | 'email_sync' | 'template' = 'manual';
  if (tripData.emailSyncEnabled) {
    creationMethod = 'email_sync';
  } else if ((tripData as any).templateId) {
    creationMethod = 'template';
  } else if ((tripData as any).fromAI) {
    creationMethod = 'ai';
  }

  // Log all successful trip creation events.
  logAnalyticsEvent('trip_created', {
    tripId: createdTripId,
    actorId: uid,
    source: creationMethod,
    result: 'success',
    isFirstTrip,
  }).catch((error) => {
    console.warn('[trips] Failed to log trip_created event', error);
  });

  // Keep first-trip activation analytics for onboarding funnel tracking.
  if (isFirstTrip) {
    logAnalyticsEvent('first_trip_created', {
      tripId: createdTripId,
      destination: finalDestination,
      method: creationMethod,
    }).catch((error) => {
      // Silently fail analytics - don't break trip creation
      console.warn('[trips] Failed to log first_trip_created event', error);
    });
  }

  // If we skipped landmark image during creation, backfill asynchronously (best-effort).
  if (tripData.skipLandmarkImage && finalDestination) {
    resolveLandmarkImageResult(finalDestination, {
      identityKey: resolveDestinationIdentity(primaryDestination),
      countryName: primaryDestination?.country,
    })
      .then((result) => {
        if (!result?.url) return;
        return setDoc(
          createdTripRef,
          sanitizeRecord({
            landmarkImage: result.url,
            landmarkImageCredit: result.creditText ?? null,
            landmarkImageCreditUrl: result.creditUrl ?? null,
            updatedAt: serverTimestamp(),
          }),
          { merge: true },
        );
      })
      .catch((error) => {
        // Best-effort; never fail trip creation because of imagery.
        console.warn('[trips] Failed to backfill landmark image after fast trip create', error);
      });
  } else if (finalDestination) {
    const landmarkImageResult = await landmarkImagePromise;
    if (landmarkImageResult?.url) {
      await setDoc(
        createdTripRef,
        sanitizeRecord({
          landmarkImage: landmarkImageResult.url,
          landmarkImageCredit: landmarkImageResult.creditText ?? null,
          landmarkImageCreditUrl: landmarkImageResult.creditUrl ?? null,
          updatedAt: serverTimestamp(),
        }),
        { merge: true },
      );
    }
  }

  triggerDestinationLandmarkPrewarm(finalDestination);

  return createdTripId;
};

// Register sync handler for trip updates
syncQueue.registerHandler('update_trip', async (action) => {
  try {
    const { tripId, updates } = action.payload as {
      tripId: string;
      updates: UpdateTripData;
    };
    const meta = (action.payload as any)?.meta as Record<string, unknown> | undefined;

    // Get the trip to verify ownership
    const tripRef = doc(db, 'trips', tripId);
    let snapshot;
    try {
      snapshot = await getDocFromServer(tripRef);
    } catch {
      snapshot = await getDoc(tripRef);
    }

    if (!snapshot.exists()) {
      throw new Error('Trip not found');
    }

    const data = snapshot.data() as FirestoreTrip;
    const serverUpdatedAtMs =
      data.updatedAt instanceof Timestamp
        ? data.updatedAt.toMillis()
        : typeof data.updatedAt === 'string'
          ? new Date(data.updatedAt).getTime()
          : 0;
    if (serverUpdatedAtMs > action.timestamp + 2_000) {
      console.warn('[Trips] Skipping stale queued trip update; server document is newer', {
        tripId,
        serverUpdatedAtMs,
        queuedAt: action.timestamp,
      });
      return;
    }

    const oldTripStart = readTripCalendarDate(data.startDate);
    // Convert updates to Firestore format
    const firestoreUpdates: Record<string, any> = {};

    if (updates.name !== undefined) firestoreUpdates.title = updates.name;
    if (updates.destination !== undefined) firestoreUpdates.destination = updates.destination;
    const startDate = coerceToValidDate((updates as any).startDate);
    const endDate = coerceToValidDate((updates as any).endDate);
    if ((updates as any).startDate !== undefined && startDate) {
      firestoreUpdates.startDate = timestampFromLocalDateOnly(startDate);
    }
    if ((updates as any).endDate !== undefined && endDate) {
      firestoreUpdates.endDate = timestampFromLocalDateOnly(endDate);
    }
    applyDestinationDateSyncToFirestoreUpdates(firestoreUpdates, data, startDate, endDate);
    if (updates.travelMode !== undefined) firestoreUpdates.travelMode = updates.travelMode;
    if (updates.preferredTravelMode !== undefined) firestoreUpdates.preferredTravelMode = updates.preferredTravelMode;
    if (updates.emailSyncEnabled !== undefined) firestoreUpdates.emailSyncEnabled = updates.emailSyncEnabled;
    if (updates.budget !== undefined) firestoreUpdates.budget = updates.budget;
    if (updates.baseCurrency !== undefined) firestoreUpdates.baseCurrency = updates.baseCurrency;
    if (updates.budgetCategoryAllocations !== undefined) {
      firestoreUpdates.budgetCategoryAllocations = updates.budgetCategoryAllocations;
    }
    if (updates.budgetAlertThresholds !== undefined) firestoreUpdates.budgetAlertThresholds = updates.budgetAlertThresholds;
    if (updates.budgetAlertState !== undefined) firestoreUpdates.budgetAlertState = updates.budgetAlertState;
    if (updates.referencePoints !== undefined) {
      firestoreUpdates.referencePoints = serializeReferencePointsForWrite(updates.referencePoints);
    }
    if (updates.primaryReferencePointId !== undefined) {
      firestoreUpdates.primaryReferencePointId = updates.primaryReferencePointId;
    }
    if (updates.distanceDisplayMode !== undefined) {
      firestoreUpdates.distanceDisplayMode = updates.distanceDisplayMode;
    }
    if (updates.walkingRadiusConfig !== undefined) {
      firestoreUpdates.walkingRadiusConfig = updates.walkingRadiusConfig;
    }

    // Destinations: handle date strings from queued offline updates
    if ((updates as any).destinations !== undefined && Array.isArray((updates as any).destinations)) {
      const destinations = (updates as any).destinations
        .map((dest: any) => {
          const destStart = coerceToValidDate(dest?.startDate);
          const destEnd = coerceToValidDate(dest?.endDate);
          if (!dest?.city || !destStart || !destEnd) {
            return null;
          }
          const out: any = {
            city: dest.city,
            startDate: timestampFromLocalDateOnly(destStart),
            endDate: timestampFromLocalDateOnly(destEnd),
          };
          if (dest.country) {
            out.country = dest.country;
          }
          if (dest.countryCode) {
            out.countryCode = dest.countryCode;
          }
          if (dest.startTime) {
            out.startTime = dest.startTime;
          }
          if (dest.endTime) {
            out.endTime = dest.endTime;
          }
          if (dest.locationId) {
            out.locationId = dest.locationId;
          }
          if (dest.placeId) {
            out.placeId = dest.placeId;
          }
          if (
            dest.coordinates &&
            typeof dest.coordinates.latitude === 'number' &&
            typeof dest.coordinates.longitude === 'number'
          ) {
            out.coordinates = {
              latitude: dest.coordinates.latitude,
              longitude: dest.coordinates.longitude,
            };
          }
          return out;
        })
        .filter(Boolean);

      firestoreUpdates.destinations = destinations;
      if (destinations.length > 0) {
        // Keep legacy destination field in sync with first city for backward compatibility.
        firestoreUpdates.destination = destinations[0].city;
      }
    }

    firestoreUpdates.updatedAt = serverTimestamp();
    firestoreUpdates.lastEditedAt = serverTimestamp();
    firestoreUpdates.lastEditedBy = action.userId;

    await setDoc(tripRef, firestoreUpdates, { merge: true });

    // Shift template itinerary in the background so sync queue processing is not blocked by
    // large itinerary scans/writes (same rationale as `updateTrip`).
    const newStart = startDate;
    const shiftAlreadyAppliedLocally = meta?.templateShiftAppliedLocally === true;
    const startDateChanged =
      (updates as any).startDate !== undefined &&
      newStart != null &&
      tripStartCalendarKeysDiffer(oldTripStart, newStart);
    if (startDateChanged && !shiftAlreadyAppliedLocally) {
      void (async () => {
        try {
          const shiftResult = await shiftTripTemplateItineraryItemDates(tripId, {
            oldStartDate: oldTripStart,
            newStartDate: newStart,
          });
          if (shiftResult.updated === 0) {
            await realignTemplateItineraryToTripStart(tripId, newStart);
          }
        } catch (error) {
          console.warn('[Trips] Failed to shift template itinerary items after synced trip date update', {
            tripId,
            error,
          });
        }
      })();
    }

    console.log(`[Trips] Synced trip update for ${tripId}`);
  } catch (error) {
    console.error('[Trips] Error syncing trip update:', error);
    throw error;
  }
});

// Update trip data
export type UpdateTripData = {
  name?: string;
  destination?: string;
  destinations?: TripDestination[];
  startDate?: Date;
  endDate?: Date;
  travelMode?: string;
  preferredTravelMode?: TravelMode;
  emailSyncEnabled?: boolean;
  budget?: number | null;
  baseCurrency?: string;
  budgetCategoryAllocations?: BudgetCategoryAllocations;
  budgetAlertThresholds?: number[];
  budgetAlertState?: BudgetAlertState;
  budgetUpdateMode?: BudgetUpdateMode;
  referencePoints?: MapReferencePoint[];
  primaryReferencePointId?: string;
  distanceDisplayMode?: DistanceDisplayMode;
  walkingRadiusConfig?: WalkingRadiusConfig;
  ownerName?: string;
};

type DeleteTripCascadeRequest = {
  tripId: string;
};

type DeleteTripCascadeResponse = {
  ok: true;
};

export const updateTrip = async (
  tripId: string,
  uid: string,
  tripData: UpdateTripData
): Promise<TripUpdateResult> => {
  // Check if offline
  if (!networkMonitor.isOnline()) {
    console.log('[Trips] Device is offline, queueing trip update');
    // Do not touch Firestore here (getDoc / shiftTripTemplateItineraryItemDates use the network and can
    // hang the UI). `queueTripUpdate` merges into the offline pack and shifts template rows in-cache only.
    await queueTripUpdate(tripId, tripData as Partial<Trip>, uid, {
      deferredFirestoreTemplateShift: true,
    });
    return 'queued';
  }

  try {
    const tripRef = doc(db, 'trips', tripId);
    let snapshot;
    try {
      snapshot = await getDocFromServer(tripRef);
    } catch {
      snapshot = await getDoc(tripRef);
    }

    if (!snapshot.exists()) {
      throw new Error('Trip not found');
    }

    const data = snapshot.data() as FirestoreTrip;
    const oldTripStart = readTripCalendarDate(data.startDate);
    const oldTripEnd = readTripCalendarDate(data.endDate);

    // Permission check:
    // - Owner can always update.
    // - Collaborators with 'edit' permission can update trip details.
    // - Back-compat: if owner is missing but user is in members, allow (older docs).
    const isOwner = data.owner === uid;
    const collaboratorPermission = data.collaborators?.find((c) => c.userId === uid)?.permission;
    const isLegacyMemberOwner = (!data.owner || typeof data.owner !== 'string') && Array.isArray(data.members) && data.members.includes(uid);
    const canEdit = isOwner || isLegacyMemberOwner || collaboratorPermission === 'edit';

    if (!canEdit) {
      throw new Error("You don't have permission to edit this trip.");
    }

    const updateData: Partial<FirestoreTrip> = {
      updatedAt: serverTimestamp(),
      lastEditedBy: uid,
      lastEditedAt: serverTimestamp(),
    };
    let landmarkBackfillTarget:
      | {
          destination: string;
          options?: ResolveImageOptions;
          countryCode?: string;
        }
      | null = null;

    // Update fields if provided
    if (tripData.name !== undefined) {
      updateData.title = tripData.name;
    }

    if (tripData.destination !== undefined) {
      updateData.destination = tripData.destination;
      if (hasDestinationChanged(tripData.destination, data.destination)) {
        landmarkBackfillTarget = {
          destination: tripData.destination,
        };
      }
    }

    if (tripData.destinations !== undefined) {
      // Convert destinations to Firestore format
      updateData.destinations = serializeTripDestinationsForWrite(tripData.destinations);
      // Also update legacy destination field with first city for backward compatibility
      if (tripData.destinations.length > 0) {
        const primaryDestination = tripData.destinations[0];
        const nextPrimaryDestination = buildDestinationQuery(primaryDestination) || primaryDestination.city;
        updateData.destination = nextPrimaryDestination;
        if (hasDestinationChanged(nextPrimaryDestination, data.destination)) {
          landmarkBackfillTarget = {
            destination: nextPrimaryDestination,
            options: {
              identityKey: resolveDestinationIdentity(primaryDestination),
              countryName: primaryDestination.country,
            },
            countryCode: primaryDestination.countryCode,
          };
        }
      }
    }

    if (tripData.startDate !== undefined) {
      updateData.startDate = timestampFromLocalDateOnly(tripData.startDate);
    }

    if (tripData.endDate !== undefined) {
      updateData.endDate = timestampFromLocalDateOnly(tripData.endDate);
    }

    if (tripData.destinations === undefined) {
      applyDestinationDateSyncToFirestoreUpdates(
        updateData as Record<string, unknown>,
        data,
        tripData.startDate,
        tripData.endDate,
      );
    }

    if (tripData.travelMode !== undefined) {
      updateData.travelMode = tripData.travelMode;
    }

    if (tripData.preferredTravelMode !== undefined) {
      updateData.preferredTravelMode = tripData.preferredTravelMode;
    }

    if (tripData.emailSyncEnabled !== undefined) {
      updateData.emailSyncEnabled = tripData.emailSyncEnabled;
    }

    if (tripData.budget !== undefined) {
      updateData.budget = tripData.budget;
    }

    if (tripData.baseCurrency !== undefined) {
      updateData.baseCurrency = tripData.baseCurrency;
    }

    if (tripData.budgetCategoryAllocations !== undefined) {
      updateData.budgetCategoryAllocations = tripData.budgetCategoryAllocations;
    }

    if (tripData.budgetAlertThresholds !== undefined) {
      updateData.budgetAlertThresholds =
        tripData.budgetAlertThresholds.length > 0
          ? tripData.budgetAlertThresholds
          : DEFAULT_BUDGET_ALERT_THRESHOLDS;
    }

    if (tripData.budgetAlertState !== undefined) {
      updateData.budgetAlertState = tripData.budgetAlertState;
    }

    if (tripData.referencePoints !== undefined) {
      updateData.referencePoints = serializeReferencePointsForWrite(tripData.referencePoints);
    }

    if (tripData.primaryReferencePointId !== undefined) {
      updateData.primaryReferencePointId = tripData.primaryReferencePointId;
    }

    if (tripData.distanceDisplayMode !== undefined) {
      updateData.distanceDisplayMode = tripData.distanceDisplayMode;
    }

    if (tripData.walkingRadiusConfig !== undefined) {
      updateData.walkingRadiusConfig = tripData.walkingRadiusConfig;
    }

    if (tripData.ownerName !== undefined) {
      updateData.ownerName = tripData.ownerName;
    }

    await setDoc(tripRef, updateData, { merge: true });

    // Resolve when Firestore has acked/rejected the write — setDoc alone only guarantees local cache.
    try {
      await waitForPendingWrites(db);
    } catch (pendingError) {
      console.warn('[Trips] Trip update pending write failed on server', { tripId, pendingError });
      throw pendingError;
    }

    if (landmarkBackfillTarget?.destination) {
      triggerDestinationLandmarkPrewarm(
        landmarkBackfillTarget.destination,
        landmarkBackfillTarget.countryCode,
      );
      // Never block trip edits on image resolution/network calls.
      resolveLandmarkImageResult(landmarkBackfillTarget.destination, landmarkBackfillTarget.options)
        .then((landmarkImageResult) => {
          if (!landmarkImageResult?.url) return;
          return setDoc(
            tripRef,
            sanitizeRecord({
              landmarkImage: landmarkImageResult.url,
              landmarkImageCredit: landmarkImageResult.creditText ?? null,
              landmarkImageCreditUrl: landmarkImageResult.creditUrl ?? null,
              updatedAt: serverTimestamp(),
            }),
            { merge: true },
          );
        })
        .catch((error) => {
          // Best-effort: trip update is already committed.
          console.warn('[Trips] Failed to backfill landmark image after trip update', {
            tripId,
            error,
          });
        });
    }

    // Re-read from server so local cache matches committed Firestore (avoids stale client cache on restart).
    let updatedTripForLiveList: Trip | null = null;
    try {
      let freshSnap;
      try {
        freshSnap = await getDocFromServer(tripRef);
      } catch {
        freshSnap = await getDoc(tripRef);
      }
      if (freshSnap.exists()) {
        updatedTripForLiveList = convertFirestoreTrip(tripId, freshSnap.data() as FirestoreTrip);
        const hasDateEdits = tripData.startDate !== undefined || tripData.endDate !== undefined;
        if (hasDateEdits && !serverTripReflectsDateEdits(updatedTripForLiveList, tripData)) {
          throw new Error(
            'Trip dates did not save to the server. Check your connection and try again.',
          );
        }
        await cacheTripData(updatedTripForLiveList);
      }
    } catch (error) {
      console.warn('[Trips] Failed to update local cache after trip update:', error);
    }

    if (updatedTripForLiveList) {
      notifyLiveTripUpdated(uid, updatedTripForLiveList);
    } else {
      const previous = getLastKnownTrips(uid)?.trips.find((trip) => trip.id === tripId);
      if (previous) {
        notifyLiveTripUpdated(uid, applyUpdateTripDataForLiveList(previous, tripData, uid));
      }
    }

    // Drop any pending offline `update_trip` rows for this trip so a restart cannot replay May dates over June.
    try {
      const removed = await syncQueue.removeActionsWhere(
        (action) => action.type === 'update_trip' && action.tripId === tripId,
      );
      if (removed > 0) {
        console.warn('[Trips] Cleared stale queued trip updates after successful save', { tripId, removed });
      }
    } catch (error) {
      console.warn('[Trips] Failed to clear queued trip updates after save', { tripId, error });
    }

    // If the trip start date changed, shift template itinerary items so Day 1 maps to new Day 1.
    // Run in the background: the trip document is already saved; awaiting here left Edit Trip on
    // "Saving..." while users could already see updated trip dates after closing the modal.
    const newStart = coerceToValidDate((tripData as any).startDate);
    const startDateChanged =
      tripData.startDate !== undefined &&
      newStart != null &&
      tripStartCalendarKeysDiffer(oldTripStart, newStart);
    if (startDateChanged) {
      void (async () => {
        try {
          const shiftResult = await shiftTripTemplateItineraryItemDates(tripId, {
            oldStartDate: oldTripStart,
            newStartDate: newStart,
          });
          if (shiftResult.updated === 0) {
            await realignTemplateItineraryToTripStart(tripId, newStart);
          }
        } catch (error) {
          console.warn('[Trips] Failed to shift template itinerary items after trip date update', {
            tripId,
            error,
          });
        }
      })();
    }

    // Update user stats in the background so post-save reads cannot keep Edit Trip on "Saving...".
    // We can decide whether to trigger using in-memory values from this mutation.
    const effectiveEndDate = coerceToValidDate((tripData as any).endDate) ?? oldTripEnd;
    const shouldRefreshUserStats =
      effectiveEndDate < new Date() ||
      tripData.destinations !== undefined ||
      tripData.destination !== undefined;
    if (shouldRefreshUserStats) {
      void import('./userStats')
        .then(({ updateUserStatsFromTrips }) =>
          fetchUserTrips(uid)
            .then(({ trips: allTrips }) => updateUserStatsFromTrips(uid, allTrips))
            .catch((error) => {
              console.warn('[Trips] Failed to refresh user stats after trip update:', error);
            }),
        )
        .catch((error) => {
          // Best-effort only; stats refresh should never block or fail trip edits.
          console.warn('[Trips] Failed to schedule user stats refresh after trip update:', error);
        });
    }

    return 'server';
  } catch (error) {
    console.error('[Trips] Error updating trip:', error);
    const code = (error as any)?.code as string | undefined;
    const isTransientNetworkError =
      code === 'unavailable' ||
      code === 'failed-precondition' ||
      code === 'deadline-exceeded' ||
      code === 'aborted';
    if (isTransientNetworkError) {
      console.warn('[Trips] Network/transient failure while updating trip; queueing update for retry', {
        tripId,
        code,
      });
      await queueTripUpdate(tripId, tripData as Partial<Trip>, uid, {
        deferredFirestoreTemplateShift: true,
        queuedAfterOnlineFailure: true,
      });
      return 'queued';
    }
    throw error;
  }
};

type ReferencePointUpdateData = Pick<
  UpdateTripData,
  'referencePoints' | 'primaryReferencePointId' | 'distanceDisplayMode' | 'walkingRadiusConfig'
>;

const serializeTripDestinationsForWrite = (destinations: TripDestination[]): FirestoreTripDestination[] => {
  return destinations.map((dest) => {
    const base: FirestoreTripDestination = {
      city: dest.city,
      startDate: timestampFromLocalDateOnly(dest.startDate),
      endDate: timestampFromLocalDateOnly(dest.endDate),
    };

    // Firestore rejects `undefined` in nested objects, so only include when present.
    if (dest.country) {
      base.country = dest.country;
    }
    if (dest.countryCode) {
      base.countryCode = dest.countryCode;
    }
    if (dest.startTime) {
      base.startTime = dest.startTime;
    }
    if (dest.endTime) {
      base.endTime = dest.endTime;
    }
    if (dest.locationId) {
      base.locationId = dest.locationId;
    }
    if (dest.placeId) {
      base.placeId = dest.placeId;
    }
    if (dest.coordinates && typeof dest.coordinates.latitude === 'number' && typeof dest.coordinates.longitude === 'number') {
      base.coordinates = {
        latitude: dest.coordinates.latitude,
        longitude: dest.coordinates.longitude,
      };
    }
    return base;
  });
};

export const updateTripReferencePoints = async (
  tripId: string,
  uid: string,
  updates: ReferencePointUpdateData,
): Promise<TripUpdateResult> => {
  return updateTrip(tripId, uid, updates);
};

export const getTripReferencePoints = async (
  tripId: string,
): Promise<{
  referencePoints: MapReferencePoint[];
  primaryReferencePointId?: string;
  distanceDisplayMode?: DistanceDisplayMode;
  walkingRadiusConfig?: WalkingRadiusConfig;
}> => {
  const tripRef = doc(db, 'trips', tripId);
  const snapshot = await getDoc(tripRef);

  if (!snapshot.exists()) {
    throw new Error('Trip not found');
  }

  const data = snapshot.data() as FirestoreTrip;
  return {
    referencePoints: convertReferencePointsFromFirestore(data.referencePoints) ?? [],
    primaryReferencePointId: data.primaryReferencePointId,
    distanceDisplayMode: data.distanceDisplayMode,
    walkingRadiusConfig: data.walkingRadiusConfig,
  };
};

// Update landmark image for an existing trip
export const updateTripLandmarkImage = async (
  tripId: string,
  destination: string,
  resolved?: LandmarkImageResult | null,
  options?: { identityKey?: string; countryName?: string | null },
): Promise<LandmarkImageResult | null> => {
  if (!destination) return null;
  const tripRef = doc(db, 'trips', tripId);
  const result =
    resolved ??
    (await resolveLandmarkImageResult(destination, {
      identityKey: options?.identityKey,
      countryName: options?.countryName,
    }));

  if (!result?.url) {
    return null;
  }

  await setDoc(
    tripRef,
    {
      landmarkImage: result.url,
      landmarkImageCredit: result.creditText ?? null,
      landmarkImageCreditUrl: result.creditUrl ?? null,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  return result;
};

// Force-refresh a trip's landmark image (clears caches and re-fetches)
export const refreshTripLandmarkImage = async (
  tripId: string,
  destination: string,
  options?: { identityKey?: string; countryName?: string | null },
): Promise<LandmarkImageResult | null> => {
  if (!destination) return null;

  try {
    // Clear local caches to avoid stale or incorrect imagery
    await clearCachedLandmarkImage(destination, {
      identityKey: options?.identityKey,
      countryName: options?.countryName,
    });

    // Force remote refresh and persist updated Firestore destination cache
    const refreshed = await fetchAndCacheLandmarkImage(destination, {
      forceRefresh: true,
      identityKey: options?.identityKey,
      countryName: options?.countryName,
    });
    if (!refreshed?.url) {
      return null;
    }

    // Save the refreshed image onto the trip document
    await updateTripLandmarkImage(tripId, destination, refreshed, options);
    return refreshed;
  } catch (error) {
    console.warn('[Trips] Failed to refresh landmark image', { destination, tripId, error });
    return null;
  }
};

// Ensure trip exists (for backward compatibility)
export const ensureTripForUser = async (uid: string): Promise<string> => {
  const tripId = `onboard-${uid}`;
  const tripRef = doc(db, 'trips', tripId);
  const snapshot = await getDoc(tripRef);

  if (!snapshot.exists()) {
    await setDoc(tripRef, {
      owner: uid,
      members: [uid],
      status: 'draft',
      title: 'Your first Tripvio itinerary',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      budget: null,
      locale: 'en-US',
    });
  }

  return tripId;
};

// Archive a trip
export const archiveTrip = async (tripId: string, uid: string): Promise<void> => {
  const tripRef = doc(db, 'trips', tripId);
  const snapshot = await getDoc(tripRef);

  if (!snapshot.exists()) {
    throw new Error('Trip not found');
  }

  const data = snapshot.data() as FirestoreTrip;
  const isLegacyMemberOwner =
    (!data.owner || typeof data.owner !== 'string') &&
    Array.isArray(data.members) &&
    data.members.includes(uid);

  // Only owner can archive. Back-compat: allow legacy owner-missing docs for listed members.
  if (data.owner !== uid && !isLegacyMemberOwner) {
    throw new Error('Only the trip owner can archive trips');
  }

  await setDoc(
    tripRef,
    {
      isArchived: true,
      updatedAt: serverTimestamp(),
      lastEditedBy: uid,
      lastEditedAt: serverTimestamp(),
    },
    { merge: true }
  );
};

// Unarchive a trip
export const unarchiveTrip = async (tripId: string, uid: string): Promise<void> => {
  const tripRef = doc(db, 'trips', tripId);
  const snapshot = await getDoc(tripRef);

  if (!snapshot.exists()) {
    throw new Error('Trip not found');
  }

  const data = snapshot.data() as FirestoreTrip;
  const isLegacyMemberOwner =
    (!data.owner || typeof data.owner !== 'string') &&
    Array.isArray(data.members) &&
    data.members.includes(uid);

  // Only owner can unarchive. Back-compat: allow legacy owner-missing docs for listed members.
  if (data.owner !== uid && !isLegacyMemberOwner) {
    throw new Error('Only the trip owner can unarchive trips');
  }

  await setDoc(
    tripRef,
    {
      isArchived: false,
      updatedAt: serverTimestamp(),
      lastEditedBy: uid,
      lastEditedAt: serverTimestamp(),
    },
    { merge: true }
  );
};

// Delete a trip permanently
export const deleteTrip = async (tripId: string, uid: string): Promise<void> => {
  const tripRef = doc(db, 'trips', tripId);
  const snapshot = await getDoc(tripRef);

  if (!snapshot.exists()) {
    throw new Error('Trip not found');
  }

  const data = snapshot.data() as FirestoreTrip;
  const isLegacyMemberOwner =
    (!data.owner || typeof data.owner !== 'string') &&
    Array.isArray(data.members) &&
    data.members.includes(uid);

  // Only owner can delete. Back-compat: allow legacy owner-missing docs for listed members.
  if (data.owner !== uid && !isLegacyMemberOwner) {
    throw new Error('Only the trip owner can delete trips');
  }

  const deleteTripCallable = httpsCallableWithTimeout<
    DeleteTripCascadeRequest,
    DeleteTripCascadeResponse
  >(functions, 'deleteTripCascade', 120000);
  await deleteTripCallable({ tripId });
  notifyLiveTripDeleted(uid, tripId);

  // Best-effort local cleanup so stale trip-scoped notifications cannot fire after deletion.
  await Promise.allSettled([
    cancelNotificationsForTrip(tripId),
    clearNotificationsForTrip(tripId),
  ]);
};

/**
 * Merge multiple trips into one (server migrates bookings, subcollections, vault links).
 */
export const mergeTrips = async (
  targetTripId: string,
  sourceTripIds: string[],
  uid: string
): Promise<void> => {
  if (sourceTripIds.length === 0) {
    return;
  }

  const targetTripRef = doc(db, 'trips', targetTripId);
  const targetSnapshot = await getDoc(targetTripRef);

  if (!targetSnapshot.exists()) {
    throw new Error('Target trip not found');
  }

  const targetData = targetSnapshot.data() as FirestoreTrip;

  if (targetData.owner !== uid) {
    throw new Error('Only the trip owner can merge trips');
  }

  for (const sourceTripId of sourceTripIds) {
    const sourceSnapshot = await getDoc(doc(db, 'trips', sourceTripId));
    if (!sourceSnapshot.exists()) {
      throw new Error(`Source trip not found: ${sourceTripId}`);
    }
    const sourceData = sourceSnapshot.data() as FirestoreTrip;
    if (sourceData.owner !== uid) {
      throw new Error(`Only the trip owner can merge trip ${sourceTripId}`);
    }
  }

  const mergeFn = httpsCallableWithTimeout<MergeTripsForOwnerRequest, MergeTripsForOwnerResponse>(
    functions,
    'mergeTripsForOwnerV1',
    120000
  );
  await mergeFn({ targetTripId, sourceTripIds });

  import('./userStats')
    .then(({ updateUserStatsFromTrips }) =>
      fetchUserTrips(uid).then(({ trips: allTrips }) =>
        updateUserStatsFromTrips(uid, allTrips).catch((error) => {
          console.warn('[trips] Failed to update user stats after merge:', error);
        })
      )
    )
    .catch((error) => {
      console.warn('[trips] Failed to schedule user stats update after merge:', error);
    });
};

export const splitTripIntoNewTrip = async (
  sourceTripId: string,
  newTripName: string,
  bookingIds: string[],
  uid: string,
  itineraryItemIds: string[] = [],
): Promise<SplitTripForOwnerResponse> => {
  if (bookingIds.length === 0 && itineraryItemIds.length === 0) {
    throw new Error('Choose at least one booking or plan item to move.');
  }
  const sourceSnapshot = await getDoc(doc(db, 'trips', sourceTripId));
  if (!sourceSnapshot.exists()) {
    throw new Error('Source trip not found');
  }
  const sourceData = sourceSnapshot.data() as FirestoreTrip;
  if (sourceData.owner !== uid) {
    throw new Error('Only the trip owner can split a trip');
  }

  const splitFn = httpsCallableWithTimeout<SplitTripForOwnerRequest, SplitTripForOwnerResponse>(
    functions,
    'splitTripForOwnerV1',
    120000
  );
  const { data } = await splitFn({
    sourceTripId,
    newTripName,
    bookingIds,
    itineraryItemIds,
  });

  import('./userStats')
    .then(({ updateUserStatsFromTrips }) =>
      fetchUserTrips(uid).then(({ trips: allTrips }) =>
        updateUserStatsFromTrips(uid, allTrips).catch((error) => {
          console.warn('[trips] Failed to update user stats after split:', error);
        })
      )
    )
    .catch((error) => {
      console.warn('[trips] Failed to schedule user stats update after split:', error);
    });

  return data;
};

export const undoLastTripMerge = async (uid: string): Promise<UndoLastTripMergeResponse> => {
  const undoFn = httpsCallableWithTimeout<Record<string, never>, UndoLastTripMergeResponse>(
    functions,
    'undoLastTripMergeV1',
    120000
  );
  const { data } = await undoFn({});

  import('./userStats')
    .then(({ updateUserStatsFromTrips }) =>
      fetchUserTrips(uid).then(({ trips: allTrips }) =>
        updateUserStatsFromTrips(uid, allTrips).catch((error) => {
          console.warn('[trips] Failed to update user stats after undo merge:', error);
        })
      )
    )
    .catch((error) => {
      console.warn('[trips] Failed to schedule user stats update after undo merge:', error);
    });

  return data;
};


